import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, collection, query, orderBy, addDoc, increment, where, arrayUnion } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ChevronLeft, ChevronRight, CheckCircle, Save, Loader2, MessageCircle, LogOut, MessageSquare, X, Send, Eye, Volume2, Play, Pause, Square, Mic, AlertCircle, TrendingUp, Zap, Clock, Brain, Layout, Image as ImageIcon, Maximize2, Settings } from 'lucide-react';
import { GroundedTutor } from './GroundedTutor';
import { PageRenderer } from './PageRenderer';
import { useDocuments } from '../contexts/DocumentContext';
import { useUpdate } from '../contexts/UpdateContext';
import { CourseContent, LevelContent, LessonDepth, QuizQuestion, generateLessonDepths, generateQuizForLesson, generateReviewQuiz } from '../services/gemini';
import { updateConceptMastery, getMasteryForLesson, updateLearningObjective } from '../services/masteryService';
import { VisualAsset, Enrollment } from '../types';
import { cleanTextForSpeech, toArray, isMobileDevice } from '../lib/utils';
import { Diagnostics, GoldenPathStep } from '../lib/diagnostics';

interface Course {
  id: string;
  title: string;
  description: string;
  orgId?: string;
  levels: any; // Can be old format or new CourseContent
  sources?: {
    pdfs?: any[];
    urls?: any[];
  };
  generationMetadata?: {
    failedLessonsCount: number;
    totalLessonsCount: number;
    isPartial: boolean;
  };
}

interface Comment {
  id: string;
  userId: string;
  userName: string;
  text: string;
  createdAt: any;
}

export function CoursePlayer() {
  const { courseId } = useParams<{ courseId: string }>();
  const { user, role } = useAuth();
  const { setBusyState } = useUpdate();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [lessonMastery, setLessonMastery] = useState<Record<string, number>>({});
  const MASTERY_THRESHOLD = 60;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // New Features State
  const [depth, setDepth] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [unlockedDepths, setUnlockedDepths] = useState<Record<string, boolean>>({ beginner: true });
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [quizError, setQuizError] = useState('');
  
  const [generatedDepths, setGeneratedDepths] = useState<Record<string, any>>({});
  const [isGeneratingDepths, setIsGeneratingDepths] = useState(false);
  const [generationFailedIdxs, setGenerationFailedIdxs] = useState<Record<string, boolean>>({});
  const [generatedQuizzes, setGeneratedQuizzes] = useState<Record<string, QuizQuestion[]>>({});
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [globalProfile, setGlobalProfile] = useState<any>(null);
  const [dueReviews, setDueReviews] = useState<any[]>([]);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isReviewQuiz, setIsReviewQuiz] = useState(false);
  const [drivingQuizStatus, setDrivingQuizStatus] = useState('');
  const [drivingQuizQuestionIndex, setDrivingQuizQuestionIndex] = useState(0);
  const [drivingQuizAttemptCount, setDrivingQuizAttemptCount] = useState(0);
  const drivingQuizActiveRef = useRef(false);
  const drivingQuizQuestionsRef = useRef<QuizQuestion[]>([]);
  const drivingQuizQuestionIndexRef = useRef(0);
  const drivingQuizAttemptCountRef = useRef(0);
  const drivingQuizRecognitionRestartRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drivingQuizListenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [learningObjectives, setLearningObjectives] = useState<any[]>([]);
  
  // All Hooks MUST be at the top level, in the same order every render.
  
  // Diagnostics State
  const [showDiag, setShowDiag] = useState(false);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'D') setShowDiag(prev => !prev);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  
  const [playState, setPlayState] = useState<'idle' | 'playing' | 'paused'>('idle');
  const playStateRef = useRef<'idle' | 'playing' | 'paused'>('idle');
  const chunksRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // New Voice Settings State
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lumina_preferred_voice') || '';
    }
    return '';
  });
  
  // Driving Mode State
  const [isDrivingMode, setIsDrivingMode] = useState(() => {
    return localStorage.getItem('lumina_driving_mode') === 'true';
  });
  const isDrivingModeRef = useRef(isDrivingMode);
  useEffect(() => {
    isDrivingModeRef.current = isDrivingMode;
  }, [isDrivingMode]);

  const [showDrivingPrompt, setShowDrivingPrompt] = useState(false);
  const promptTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isWaitingForVoiceCommand, setIsWaitingForVoiceCommand] = useState(false);
  const recognitionRef = useRef<any>(null);
  const autoPlayRef = useRef<boolean>(false);
  const wakeLockRef = useRef<any>(null);
  const wakeLockRequestedRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dummyOscillatorRef = useRef<OscillatorNode | null>(null);

  useEffect(() => {
    const isBusy = isGeneratingDepths || isGeneratingQuiz || isQuizModalOpen || isDrivingMode || !!newComment.length;
    setBusyState('course_player', isBusy);
    return () => setBusyState('course_player', false);
  }, [isGeneratingDepths, isGeneratingQuiz, isQuizModalOpen, isDrivingMode, newComment.length, setBusyState]);

  const requestDrivingWakeLock = async (reason: string = 'unspecified') => {
    wakeLockRequestedRef.current = true;

    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      console.warn('[DrivingMode] wake_lock_unavailable', { reason });
      return;
    }

    if (wakeLockRef.current) {
      return;
    }

    try {
      wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      console.log('[DrivingMode] wake_lock_acquired', { reason });

      wakeLockRef.current.addEventListener('release', () => {
        console.log('[DrivingMode] wake_lock_released_by_browser');
        wakeLockRef.current = null;
      });
    } catch (error) {
      console.warn('[DrivingMode] wake_lock_failed', { reason, error });
      wakeLockRef.current = null;
    }
  };

  const releaseDrivingWakeLock = async (reason: string = 'unspecified') => {
    wakeLockRequestedRef.current = false;

    if (!wakeLockRef.current) {
      return;
    }

    try {
      await wakeLockRef.current.release();
      console.log('[DrivingMode] wake_lock_released', { reason });
    } catch (error) {
      console.warn('[DrivingMode] wake_lock_release_failed', { reason, error });
    } finally {
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        isDrivingModeRef.current &&
        playStateRef.current === 'playing' &&
        wakeLockRequestedRef.current
      ) {
        requestDrivingWakeLock('visibility_return');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseDrivingWakeLock('course_player_unmount');
    };
  }, []);


  // New strict reset block to clear stale content when courseID changes
  useEffect(() => {
    setCourse(null);
    setEnrollment(null);
    setLessonMastery({});
    setDepth('beginner');
    setUnlockedDepths({ beginner: true });
    setGeneratedDepths({});
    setGenerationFailedIdxs({});
    setQuizAnswers([]);
    setHasPromptedVisuals({});
    currentPromptContextRef.current = 'none';
    setDueReviews([]);
    setIsReviewMode(false);
    setIsReviewQuiz(false);
    setShowCourseCompleteModal(false);
    chunksRef.current = [];
    currentChunkIndexRef.current = 0;
    handleStop();
    setLoading(true);
  }, [courseId]);

  const visualPromptAttemptsRef = useRef(0);

  const { getDocument } = useDocuments();
  const currentPromptContextRef = useRef<'visual' | 'quiz' | 'nextLesson' | 'drivingQuiz' | 'none'>('none');
  const [hasPromptedVisuals, setHasPromptedVisuals] = useState<Record<string, boolean>>({});
  const [showCourseCompleteModal, setShowCourseCompleteModal] = useState(false);
  const [showSkipWarningModal, setShowSkipWarningModal] = useState(false);
  const [showLockedWarningModal, setShowLockedWarningModal] = useState(false);

  const checkIsLastLesson = () => {
    if (!course || !enrollment) return false;
    const { currentLevel, currentLessonIndex } = enrollment;
    const levelData = course.levels?.[currentLevel];
    const isOldFormat = Array.isArray(levelData);
    const currentLessons = isOldFormat ? levelData : levelData?.lessons || [];
    
    const hasLevel = (lvl: string) => {
        const d = course.levels?.[lvl];
        if (Array.isArray(d)) return d.length > 0;
        return d && d.lessons && (d.lessons as any[]).length > 0;
    }

    if (currentLessonIndex < currentLessons.length - 1) return false;
    if (currentLevel === 'explorer' && hasLevel('apprentice')) return false;
    if (currentLevel === 'apprentice' && hasLevel('master')) return false;
    return true;
  };

  useEffect(() => {
    if (loading || !course || !enrollment) return;
    
    const lKey = enrollment?.currentLevel || 'explorer';
    const lData = course?.levels?.[lKey] || (course as any)?.[lKey];
    const isOld = Array.isArray(lData);
    const lLessons = isOld ? lData : lData?.lessons || [];
    const currentLesson = lLessons[enrollment.currentLessonIndex];
    const lessonKey = `${lKey}-${enrollment.currentLessonIndex}`;

    // Visual intelligence is disabled for learner MVP
    /*
    if (currentLesson?.visuals?.length > 0 && !hasPromptedVisuals[lessonKey]) {
      if (!isDrivingMode) {
        setTimeout(() => {
          setHasPromptedVisuals(prev => ({ ...prev, [lessonKey]: true }));
          const message = `I noticed this lesson includes an important ${currentLesson.visuals[0].type}. Would you like me to explain it before we continue?`;
          window.dispatchEvent(new CustomEvent('askTutor', { detail: { message, silent: true } }));
        }, 3000);
      }
    }
    */
  }, [enrollment?.currentLessonIndex, enrollment?.currentLevel, loading, course, isDrivingMode, hasPromptedVisuals]);

  useEffect(() => {
    if (isMobileDevice()) {
      console.log('Mobile learning access allowed');
    } else {
      console.log('Desktop learning access allowed');
    }

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      if (availableVoices.length === 0) return;
      
      setVoices(availableVoices);
      const savedVoice = localStorage.getItem('lumina_preferred_voice');
      if (savedVoice && availableVoices.find(v => v.voiceURI === savedVoice)) {
        setSelectedVoiceURI(savedVoice);
      } else {
        const ukVoice = availableVoices.find(v => v.lang === 'en-GB' && (v.name.includes('Google') || v.name.includes('Natural')));
        const defaultVoice = ukVoice || availableVoices.find(v => v.lang === 'en-GB') || availableVoices.find(v => v.name.includes('Google') || v.name.includes('Natural')) || availableVoices.find(v => v.lang.startsWith('en')) || availableVoices[0];
        if (defaultVoice) {
          setSelectedVoiceURI(defaultVoice.voiceURI);
        }
      }
    };

    loadVoices();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    const handleDrivingModeChange = (e: any) => {
      setIsDrivingMode(e.detail);
    };
    window.addEventListener('drivingModeChanged' as any, handleDrivingModeChange);
    
    // Setup Speech Recognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
    }

    return () => {
      window.removeEventListener('drivingModeChanged' as any, handleDrivingModeChange);
    };
  }, []);

  const speakText = (text: string, onEndCallback?: () => void) => {
    if (!('speechSynthesis' in window)) return;
    
    // Broadcast my turn
    const event = new CustomEvent('audioConflictStart', { detail: 'course' });
    window.dispatchEvent(event);

    window.speechSynthesis.cancel();
    const spokenText = cleanTextForSpeech(text);
    const utterance = new SpeechSynthesisUtterance(spokenText);
    
    const availableVoices = window.speechSynthesis.getVoices();
    const preferredVoice = availableVoices.find(v => v.voiceURI === selectedVoiceURI) || 
                           availableVoices.find(v => v.lang === 'en-GB') || 
                           availableVoices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    utterance.rate = 0.95;
    
    if (onEndCallback) {
      utterance.onend = onEndCallback;
    }
    
    window.speechSynthesis.speak(utterance);
  };

  const handleVisualExplanation = () => {
    if (!currentContent || !(currentContent as any).visuals || (currentContent as any).visuals.length === 0) {
      triggerNextContextPrompt();
      return;
    }
    
    const visual = (currentContent as any).visuals[0];
    const explanation = visual.interpretation?.teaching_explanation || visual.interpretation?.summary || "Analyzing visual components...";
    
    console.log("User accepted visual explanation");
    
    // Show in UI
    const message = `Explain the ${visual.type} on page ${visual.page_number} of the source document: ${explanation}`;
    window.dispatchEvent(new CustomEvent('askTutor', { detail: { message } }));
    
    // Speak explanation
    speakText(explanation, () => {
      // After explanation, continue to next prompt
      triggerNextContextPrompt();
    });
  };

  const triggerNextContextPrompt = () => {
    console.log("Proceeding to quiz/next lesson prompt");
    if (depth !== 'advanced') {
      currentPromptContextRef.current = 'quiz';
      speakText("Would you like to take a quick test, or move to the next lesson?", () => {
        startVoiceCommandListener();
      });
    } else {
      currentPromptContextRef.current = 'nextLesson';
      speakText("Would you like to take the final course assessment?", () => {
        startVoiceCommandListener();
      });
    }
  };

  const startVoiceCommandListener = () => {
    if (!recognitionRef.current || isWaitingForVoiceCommand) return;
    
    setIsWaitingForVoiceCommand(true);
    console.log(`Prompt Context: ${currentPromptContextRef.current}`);
    
    recognitionRef.current.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.toLowerCase().trim();
      console.log('Voice Response Captured:', transcript);
      
      if (transcript.includes('yes') || transcript.includes('quiz') || transcript.includes('test') || transcript.includes('knowledge check')) {
        setIsWaitingForVoiceCommand(false);
        if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
        setShowDrivingPrompt(false);
        
        if (currentPromptContextRef.current === 'visual') {
          visualPromptAttemptsRef.current = 0;
          handleVisualExplanation();
        } else if (currentPromptContextRef.current === 'quiz' || currentPromptContextRef.current === 'nextLesson') {
          console.log("User selected: Test (Voice)", { currentPromptContext: currentPromptContextRef.current });
          triggerQuiz();
        }
      } else if (transcript.includes('no') || transcript.includes('skip') || transcript.includes('next') || transcript.includes('move on') || transcript.includes('continue')) {
        setIsWaitingForVoiceCommand(false);
        if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
        setShowDrivingPrompt(false);
        
        if (currentPromptContextRef.current === 'visual') {
          console.log("User skipped visual explanation");
          visualPromptAttemptsRef.current = 0;
          triggerNextContextPrompt();
        } else if (currentPromptContextRef.current === 'quiz' || currentPromptContextRef.current === 'nextLesson') {
          console.log("User selected: Next Lesson (Voice No/Skip/Next)");
          if (checkIsLastLesson() && depth === 'advanced') {
            setShowCourseCompleteModal(true);
            if (enrollment) saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, true);
            speakText("Thank you for taking this course. Your progress has been saved. You can return anytime to review the lessons or take the final assessment later.", () => {
              updatePlayState('idle');
            });
          } else if (depth === 'advanced') {
            speakText("Thank you for completing this course. We'd love your feedback. Please take a moment to rate and review your experience.", () => {
              updatePlayState('idle');
            });
          } else {
            speakText("Moving to the next lesson.", () => {
              autoPlayRef.current = true;
              moveToNextStage();
            });
          }
        }
      } else if (transcript.includes('repeat') || transcript.includes('say that again') || transcript.includes('replay')) {
        setIsWaitingForVoiceCommand(false);
        if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
        setShowDrivingPrompt(false);
        
        console.log("User selected: Repeat Lesson");
        // To replay, basically we just start playing the first chunk again
        updatePlayState('playing');
        currentChunkIndexRef.current = 0;
        playNextChunk();
      } else if (transcript.includes('stop') || transcript.includes('exit') || transcript.includes('leave') || transcript.includes('pause')) {
        setIsWaitingForVoiceCommand(false);
        if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
        setShowDrivingPrompt(false);
        
        console.log("User selected: Stop/Exit Driving Mode");
        if (transcript.includes('pause')) {
           handlePause();
        } else {
           setIsDrivingMode(false);
           handleStop();
        }
      } else {
        // Unrecognized command
        setIsWaitingForVoiceCommand(false);
        // We handle timeout for repeat logic
      }
    };
    
    recognitionRef.current.onerror = (event: any) => {
      if (event.error === 'no-speech') {
        // Ignoring no-speech as it's common and harmless, let timeout handle auto-skip
        return;
      }
      console.error('Speech recognition error', event.error);
      setIsWaitingForVoiceCommand(false);
      // Let timeout handle it
    };
    
    recognitionRef.current.onend = () => {
      // We rely on our own timeout for flow control
    };

    try {
      recognitionRef.current.start();
      
      // Handle timeout (10 seconds as requested)
      setTimeout(() => {
        setIsWaitingForVoiceCommand(prev => {
          if (prev && recognitionRef.current) {
            recognitionRef.current.stop();
            
            // Timeout logic for visual prompt repeat
            if (currentPromptContextRef.current === 'visual') {
              if (visualPromptAttemptsRef.current < 1) {
                visualPromptAttemptsRef.current++;
                console.log("Visual prompt timeout - repeating once");
                const currentLesson = currentLevelLessons[enrollment.currentLessonIndex];
                const message = `Just checking - would you like me to explain the ${currentLesson?.visuals?.[0]?.type || 'visual'} before we continue?`;
                speakText(message, () => {
                  startVoiceCommandListener();
                });
              } else {
                console.log("Visual prompt timeout - skipping after repeat");
                visualPromptAttemptsRef.current = 0;
                triggerNextContextPrompt();
              }
            } else {
              updatePlayState('idle');
            }
          }
          return false;
        });
      }, 10000);
    } catch (e) {
      console.error("Failed to start recognition", e);
      setIsWaitingForVoiceCommand(false);
    }
  };


  const normalizeDrivingQuizText = (value?: string) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getDrivingQuizCorrectIndex = (question: QuizQuestion) => {
    if (typeof question.correctAnswerIndex === 'number') {
      return question.correctAnswerIndex;
    }

    const correctAnswer = normalizeDrivingQuizText((question as any).correctAnswer);
    const options = question.options || [];

    if (!correctAnswer) {
      return -1;
    }

    return options.findIndex((option: string) => normalizeDrivingQuizText(option) === correctAnswer);
  };

  const isDrivingQuizAnswerCorrect = (question: QuizQuestion, optionIndex: number) => {
    const correctIndex = getDrivingQuizCorrectIndex(question);
    return correctIndex >= 0 && optionIndex === correctIndex;
  };

  const getDrivingQuizOptionLabel = (index: number) => {
    return ['A', 'B', 'C', 'D', 'E', 'F'][index] || String(index + 1);
  };

  const parseDrivingQuizAnswer = (transcript: string, question: QuizQuestion) => {
    const normalized = normalizeDrivingQuizText(transcript);
    const options = question.options || [];

    const optionTerms: Array<{ index: number; terms: string[] }> = [
      { index: 0, terms: ['a', 'ay', 'hey', 'eh', 'option a', 'option ay', 'letter a', 'letter ay', 'answer a', 'answer ay', 'one', 'option one', 'option 1', 'number one', 'number 1', 'first', 'first option'] },
      { index: 1, terms: ['b', 'be', 'bee', 'option b', 'option be', 'option bee', 'letter b', 'letter be', 'answer b', 'answer be', 'two', 'option two', 'option 2', 'number two', 'number 2', 'second', 'second option'] },
      { index: 2, terms: ['c', 'see', 'sea', 'cee', 'option c', 'option see', 'option sea', 'letter c', 'letter see', 'answer c', 'answer see', 'three', 'option three', 'option 3', 'number three', 'number 3', 'third', 'third option'] },
      { index: 3, terms: ['d', 'dee', 'de', 'option d', 'option dee', 'letter d', 'letter dee', 'answer d', 'answer dee', 'four', 'option four', 'option 4', 'number four', 'number 4', 'fourth', 'fourth option'] }
    ];

    for (const option of optionTerms) {
      if (option.index >= options.length) continue;

      for (const term of option.terms) {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp('(^|\\s)' + escaped + '(\\s|$)');

        if (pattern.test(normalized)) {
          return option.index;
        }
      }
    }

    for (let i = 0; i < options.length; i += 1) {
      const optionText = normalizeDrivingQuizText(options[i]);

      if (optionText.length > 4 && (normalized.includes(optionText) || optionText.includes(normalized))) {
        return i;
      }
    }

    return null;
  };

  const recordDrivingQuizAnswerProgress = async (
    question: QuizQuestion,
    questionIndex: number,
    selectedOptionIndex: number,
    isCorrect: boolean
  ) => {
    if (!user || !courseId) return;

    try {
      const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
      await setDoc(enrollmentRef, {
        quizAttempts: increment(1),
        lastAccessed: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('[DrivingMode] quiz_attempt_tracking_failed', error);
    }

    console.log(`[DrivingMode] voice_quiz_answer question=${questionIndex + 1} selected=${selectedOptionIndex} correct=${isCorrect}`);

    Diagnostics.logStep(GoldenPathStep.QUIZ_TAKEN, 'success', {
      isCorrect,
      questionIndex,
      drivingMode: true
    });

    const qMetadata = {
      difficulty: (question as any).difficulty,
      type: (question as any).type,
      drivingMode: true
    };

    try {
      if (currentLessonStructure?.concepts) {
        for (const concept of currentLessonStructure.concepts) {
          await updateConceptMastery(
            user.uid,
            courseId,
            concept.concept_title,
            isCorrect ? 'correct_quiz' : 'incorrect_quiz',
            qMetadata
          );
        }

        const newScores = await getMasteryForLesson(user.uid, courseId, currentLessonStructure.concepts || []);
        const scoreValues = Object.values(newScores).map((value: any) => Number(value || 0));
        const avgScore = scoreValues.reduce((sum: number, value: number) => sum + value, 0) / (scoreValues.length || 1);

        if (currentLessonStructure.learning_objectives) {
          for (const [idx, objText] of currentLessonStructure.learning_objectives.entries()) {
            await updateLearningObjective(
              user.uid,
              courseId,
              (currentLessonStructure as any).lessonId || currentLessonStructure.lesson_title,
              { id: `obj_${idx}`, text: objText },
              avgScore
            );
          }
        }

        setLessonMastery(newScores);
      }
    } catch (error) {
      console.error('[DrivingMode] voice_quiz_mastery_update_failed', error);
    }
  };

  const submitDrivingQuizAnswer = async (
    question: QuizQuestion,
    questionIndex: number,
    selectedOptionIndex: number
  ) => {
    const isCorrect = isDrivingQuizAnswerCorrect(question, selectedOptionIndex);

    setQuizAnswers(prev => {
      const next = [...prev];
      next[questionIndex] = selectedOptionIndex;
      return next;
    });

    setQuizError('');
    await recordDrivingQuizAnswerProgress(question, questionIndex, selectedOptionIndex, isCorrect);

    return isCorrect;
  };

  const completeDrivingQuizAndContinue = async () => {
    drivingQuizActiveRef.current = false;
    currentPromptContextRef.current = 'none';
    setDrivingQuizStatus('Knowledge check completed. Moving to the next lesson.');
    setDrivingQuizAttemptCount(0);
    setIsWaitingForVoiceCommand(false);

    if (drivingQuizRecognitionRestartRef.current) {
      clearTimeout(drivingQuizRecognitionRestartRef.current);
      drivingQuizRecognitionRestartRef.current = null;
    }

    if (drivingQuizListenTimeoutRef.current) {
      clearTimeout(drivingQuizListenTimeoutRef.current);
      drivingQuizListenTimeoutRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {}
    }

    if (user && courseId && enrollment) {
      const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
      setDoc(enrollmentRef, {
        knowledgeCheckCompletedLessonIds: arrayUnion(`${enrollment.currentLevel}_${enrollment.currentLessonIndex}_${depth}`)
      }, { merge: true }).catch(error => console.error('[DrivingMode] knowledge_check_completion_tracking_failed', error));
    }

    setIsQuizModalOpen(false);
    resetTempVisualQuiz();

    if (checkIsLastLesson() && depth === 'advanced') {
      setShowCourseCompleteModal(true);

      if (enrollment) {
        saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, true);
      }

      speakText('Knowledge check completed. You have completed this course.', () => {
        updatePlayState('idle');
      });

      return;
    }

    speakText('Knowledge check completed. Moving to the next lesson.', () => {
      autoPlayRef.current = true;
      handleMarkAsComplete();
    });
  };

  const moveToNextDrivingQuizQuestion = () => {
    const questions = drivingQuizQuestionsRef.current;
    const nextIndex = drivingQuizQuestionIndexRef.current + 1;

    if (nextIndex >= questions.length) {
      completeDrivingQuizAndContinue();
      return;
    }

    drivingQuizQuestionIndexRef.current = nextIndex;
    drivingQuizAttemptCountRef.current = 0;
    setDrivingQuizQuestionIndex(nextIndex);
    setDrivingQuizAttemptCount(0);

    speakDrivingQuizQuestion();
  };

  const getDrivingQuizQuestionSpeech = () => {
    const questions = drivingQuizQuestionsRef.current;
    const questionIndex = drivingQuizQuestionIndexRef.current;
    const question = questions[questionIndex];

    if (!question) return '';

    const optionsSpeech = (question.options || [])
      .map((option: string, optionIndex: number) => {
        return `Option ${getDrivingQuizOptionLabel(optionIndex)}. ${cleanTextForSpeech(option)}.`;
      })
      .join(' ');

    return `Question ${questionIndex + 1} of ${questions.length}. ${cleanTextForSpeech(question.question)}. ${optionsSpeech} Please wait for the beep, then say the option letter, for example A, B, C, or D.`;
  };

  const startDrivingQuizAnswerListener = () => {
    if (!drivingQuizActiveRef.current) return;

    if (!recognitionRef.current) {
      setDrivingQuizStatus('Voice answers are not available in this browser. Please use the on-screen options.');
      speakText('Voice answers are not available in this browser. Please use the on-screen options.');
      return;
    }

    if (drivingQuizRecognitionRestartRef.current) {
      clearTimeout(drivingQuizRecognitionRestartRef.current);
      drivingQuizRecognitionRestartRef.current = null;
    }

    if (drivingQuizListenTimeoutRef.current) {
      clearTimeout(drivingQuizListenTimeoutRef.current);
      drivingQuizListenTimeoutRef.current = null;
    }

    const recognition = recognitionRef.current;

    try {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;

      if (typeof recognition.abort === 'function') {
        recognition.abort();
      } else {
        recognition.stop();
      }
    } catch (error) {
      // This only resets any previous recognition session.
    }

    setIsWaitingForVoiceCommand(true);
    currentPromptContextRef.current = 'drivingQuiz';

    drivingQuizRecognitionRestartRef.current = setTimeout(() => {
      if (!drivingQuizActiveRef.current || !recognitionRef.current) return;

      const activeRecognition = recognitionRef.current;

      const clearListenTimeout = () => {
        if (drivingQuizListenTimeoutRef.current) {
          clearTimeout(drivingQuizListenTimeoutRef.current);
          drivingQuizListenTimeoutRef.current = null;
        }
      };

      activeRecognition.onresult = (event: any) => {
        clearListenTimeout();

        const transcript = event.results[0][0].transcript.toLowerCase().trim();
        console.log('[DrivingMode] voice_quiz_answer_captured', transcript);

        setIsWaitingForVoiceCommand(false);

        try {
          activeRecognition.stop();
        } catch (error) {}

        handleDrivingQuizAnswer(transcript);
      };

      activeRecognition.onerror = (event: any) => {
        clearListenTimeout();

        console.error('[DrivingMode] voice_quiz_recognition_error', event.error);
        setIsWaitingForVoiceCommand(false);

        if (!drivingQuizActiveRef.current) return;

        if (event.error === 'no-speech') {
          speakText('I did not hear your answer. Please wait for the beep, then say A, B, C, or D.', () => {
            startDrivingQuizAnswerListener();
          });
          return;
        }

        speakText('I had trouble hearing your answer. Please wait for the beep, then say the option letter again.', () => {
          startDrivingQuizAnswerListener();
        });
      };

      activeRecognition.onend = () => {
        // The quiz flow explicitly restarts recognition after tutor speech, timeout, or recognition errors.
      };

      try {
        activeRecognition.start();
        console.log('[DrivingMode] voice_quiz_listening_started');

        drivingQuizListenTimeoutRef.current = setTimeout(() => {
          if (!drivingQuizActiveRef.current) return;

          console.warn('[DrivingMode] voice_quiz_listen_timeout');
          setIsWaitingForVoiceCommand(false);

          try {
            activeRecognition.stop();
          } catch (error) {}

          speakText('I did not hear an answer. Please wait for the beep, then say A, B, C, or D.', () => {
            startDrivingQuizAnswerListener();
          });
        }, 10000);
      } catch (error) {
        clearListenTimeout();

        console.error('[DrivingMode] voice_quiz_recognition_start_failed', error);
        setIsWaitingForVoiceCommand(false);

        if (drivingQuizActiveRef.current) {
          speakText('I could not restart voice listening. Please wait for the beep, then try saying your answer again.', () => {
            startDrivingQuizAnswerListener();
          });
        }
      }
    }, 650);
  };

  const handleDrivingQuizAnswer = async (transcript: string) => {
    const questions = drivingQuizQuestionsRef.current;
    const questionIndex = drivingQuizQuestionIndexRef.current;
    const question = questions[questionIndex];

    if (!question) {
      completeDrivingQuizAndContinue();
      return;
    }

    const selectedOptionIndex = parseDrivingQuizAnswer(transcript, question);

    if (selectedOptionIndex === null) {
      setDrivingQuizStatus('Answer not recognized. Please say A, B, C, or D.');
      speakText('I did not understand that answer. Please wait for the beep, then say A, B, C, or D.', () => {
        startDrivingQuizAnswerListener();
      });
      return;
    }

    const isCorrect = await submitDrivingQuizAnswer(question, questionIndex, selectedOptionIndex);

    if (isCorrect) {
      drivingQuizAttemptCountRef.current = 0;
      setDrivingQuizAttemptCount(0);
      setDrivingQuizStatus('Correct. Moving to the next question.');

      speakText('That is correct. Moving to the next question.', () => {
        moveToNextDrivingQuizQuestion();
      });

      return;
    }

    const attemptCount = drivingQuizAttemptCountRef.current + 1;
    drivingQuizAttemptCountRef.current = attemptCount;
    setDrivingQuizAttemptCount(attemptCount);

    if (attemptCount < 3) {
      setDrivingQuizStatus(`That answer is not correct. Attempt ${attemptCount} of 3.`);
      speakText('That is not correct. Please try again. ' + getDrivingQuizQuestionSpeech(), () => {
        startDrivingQuizAnswerListener();
      });

      return;
    }

    const correctIndex = getDrivingQuizCorrectIndex(question);
    const correctAnswerText = correctIndex >= 0 && question.options ? question.options[correctIndex] : 'the correct answer';
    const explanation = cleanTextForSpeech((question as any).explanation || 'This answer best matches the lesson content.');

    if (correctIndex >= 0) {
      setQuizAnswers(prev => {
        const next = [...prev];
        next[questionIndex] = correctIndex;
        return next;
      });
    }

    drivingQuizAttemptCountRef.current = 0;
    setDrivingQuizAttemptCount(0);
    setDrivingQuizStatus('Correct answer explained. Moving to the next question.');

    speakText(
      `That is still not correct. The correct answer is ${cleanTextForSpeech(correctAnswerText)}. ${explanation} Moving to the next question.`,
      () => {
        moveToNextDrivingQuizQuestion();
      }
    );
  };

  const speakDrivingQuizQuestion = () => {
    const questions = drivingQuizQuestionsRef.current;
    const questionIndex = drivingQuizQuestionIndexRef.current;
    const question = questions[questionIndex];

    if (!question) {
      completeDrivingQuizAndContinue();
      return;
    }

    setDrivingQuizQuestionIndex(questionIndex);
    setDrivingQuizAttemptCount(drivingQuizAttemptCountRef.current);
    setDrivingQuizStatus(`Listening for answer to question ${questionIndex + 1} of ${questions.length}.`);

    speakText(getDrivingQuizQuestionSpeech(), () => {
      startDrivingQuizAnswerListener();
    });
  };

  const beginDrivingQuiz = (questions: QuizQuestion[]) => {
    if (!questions || questions.length === 0) {
      setDrivingQuizStatus('Questions are unavailable for this lesson.');
      speakText('Questions are unavailable for this lesson. Moving to the next lesson.', () => {
        autoPlayRef.current = true;
        moveToNextStage();
      });
      return;
    }

    console.log('[DrivingMode] voice_quiz_started', { questionCount: questions.length });

    drivingQuizActiveRef.current = true;
    drivingQuizQuestionsRef.current = questions;
    drivingQuizQuestionIndexRef.current = 0;
    drivingQuizAttemptCountRef.current = 0;

    setDrivingQuizQuestionIndex(0);
    setDrivingQuizAttemptCount(0);
    setDrivingQuizStatus('Voice quiz started.');

    speakText('Starting your knowledge check. I will read each question and the answer options aloud.', () => {
      speakDrivingQuizQuestion();
    });
  };


  const triggerQuiz = async (isReview: boolean = false, forceRegenerate: boolean = false) => {
    setIsQuizModalOpen(true);
    setQuizError('');
    setIsReviewQuiz(isReview);
    
    if (isReview) {
      if (forceRegenerate || !(generatedQuizzes['review-session']?.length > 0)) {
         setIsGeneratingQuiz(true);
         try {
           const reviewQuiz = await generateReviewQuiz(dueReviews);
           setGeneratedQuizzes(prev => ({ ...prev, ['review-session']: reviewQuiz }));
         } catch (err) {
           setQuizError('Failed to generate review quiz.');
         } finally {
           setIsGeneratingQuiz(false);
         }
      }
      setQuizAnswers(new Array(3).fill(-1));
      return;
    }

    const currentQuizKey = `${courseId}-${enrollment?.currentLevel}-${enrollment?.currentLessonIndex}-${depth}`;
    let quizToUse = forceRegenerate ? [] : (generatedQuizzes[currentQuizKey] || []);
    
    if (!quizToUse || quizToUse.length === 0) {
      setIsGeneratingQuiz(true);
      try {
        const levelData = course?.levels?.[enrollment?.currentLevel || 'explorer'];
        const isOldFormat = Array.isArray(levelData);
        const currentLevelLessons = isOldFormat ? levelData : levelData?.lessons || [];
        const currentContent = currentLevelLessons[enrollment?.currentLessonIndex || 0];
        
        if (!currentContent) {
          throw new Error("No lesson content available for quiz generation");
        }

        const depthContent = (currentContent as any)?.[depth]?.audio || currentContent;
        
        const newQuiz = await generateQuizForLesson(depthContent, (currentContent as any).visuals || []);
        setGeneratedQuizzes(prev => ({ ...prev, [currentQuizKey]: newQuiz }));
        quizToUse = newQuiz;
      } catch (err) {
        setQuizError('Failed to generate quiz. Please try again.');
        quizToUse = [];
      } finally {
        setIsGeneratingQuiz(false);
      }
    }
    setQuizAnswers(new Array(quizToUse?.length || 0).fill(-1));

    if (isDrivingModeRef.current && !isReview) {
      beginDrivingQuiz(quizToUse || []);
    }
  };

  const moveToNextStage = () => {
    Diagnostics.logStep(GoldenPathStep.LEARNER_STUDY, 'success', { depth });

    const isFlexible = (course as any)?.progressionMode === 'flexible' || !(course as any)?.progressionMode;
    const hasCompletedAnyMastery = Object.keys(lessonMastery).length > 0;
    
    if (isFlexible && !hasCompletedAnyMastery && user && courseId && enrollment) {
       try {
           const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
           setDoc(enrollmentRef, {
               skippedKnowledgeCheckLessonIds: arrayUnion(`${enrollment.currentLevel}_${enrollment.currentLessonIndex}_${depth}`)
           }, { merge: true }).catch(err => console.error("Failed to track knowledge check skipping:", err));
       } catch (e) { console.error(e); }
    }

    Diagnostics.logStep(GoldenPathStep.PROGRESS_SAVED, 'success', { action: 'handleMarkAsComplete' });
    handleMarkAsComplete();
  };

  const updatePlayState = (state: 'idle' | 'playing' | 'paused') => {
    playStateRef.current = state;
    setPlayState(state);

    if (state === 'playing' && isDrivingModeRef.current) {
      requestDrivingWakeLock('play_state_playing');
    }

    if (state === 'paused') {
      releaseDrivingWakeLock('play_state_paused');
    }

    if (state === 'idle' && !isDrivingModeRef.current) {
      releaseDrivingWakeLock('play_state_idle');
    }
  };

  const playNextChunk = () => {
    if (playStateRef.current !== 'playing') return;
    if (currentChunkIndexRef.current >= chunksRef.current.length) {
      updatePlayState('idle');
      
      // Driving Mode Logic when audio finishes
      if (isDrivingModeRef.current) {
        console.log("Driving Mode Prompt Triggered");
        setShowDrivingPrompt(true);
        
        const currentLevelIdx = enrollment.currentLessonIndex;
        const lessonKey = `${enrollment.currentLevel}-${currentLevelIdx}`;
        // Re-derive to ensure absolute latest data
        const levelData = course?.levels?.[enrollment?.currentLevel || 'explorer'];
        const currentLevelLessons = Array.isArray(levelData) ? levelData : levelData?.lessons || [];
        const currentLesson = currentLevelLessons[currentLevelIdx];

        setTimeout(() => {
          /* VISUAL INTELLIGENCE DISABLED FOR PILOT
          if (currentLesson?.visuals?.length > 0 && !hasPromptedVisuals[lessonKey]) {
            console.log("Driving Visual Prompt Triggered (End of Content)");
            setHasPromptedVisuals(prev => ({ ...prev, [lessonKey]: true }));
            currentPromptContextRef.current = 'visual';
            const message = `I noticed this lesson includes an important ${currentLesson.visuals[0].type}. Would you like me to explain it before we continue?`;
            speakText(message, () => {
              startVoiceCommandListener();
            });
          } else */ if (depth !== 'advanced') {
            currentPromptContextRef.current = 'quiz';
            speakText("Would you like to take a quick test, or move to the next lesson?", () => {
              startVoiceCommandListener();
            });
          } else {
            currentPromptContextRef.current = 'nextLesson';
            speakText("Would you like to take the final course assessment?", () => {
              startVoiceCommandListener();
            });
          }
        }, 500);
        
        // Auto-next timeout
        const timer = setTimeout(() => {
          if (!isDrivingModeRef.current) return;
          setShowDrivingPrompt(false);
          setIsWaitingForVoiceCommand(false);
          if (recognitionRef.current) recognitionRef.current.stop();
          
          console.log("User selected: Next Lesson (Timeout Auto-select)");
          if (checkIsLastLesson() && depth === 'advanced') {
            setShowCourseCompleteModal(true);
            if (enrollment) saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, true);
            speakText("Course completed.", () => {
              updatePlayState('idle');
            });
          } else {
            speakText("Moving to the next lesson automatically.", () => {
              autoPlayRef.current = true;
              moveToNextStage();
            });
          }
        }, 15000);
        promptTimeoutRef.current = timer;
      }
      return;
    }

    const text = chunksRef.current[currentChunkIndexRef.current];
    const utterance = new SpeechSynthesisUtterance(text);
    
    const availableVoices = window.speechSynthesis.getVoices();
    const preferredVoice = availableVoices.find(v => v.voiceURI === selectedVoiceURI) || 
                           availableVoices.find(v => v.lang === 'en-GB') || 
                           availableVoices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    utterance.rate = 0.95;

    utterance.onend = () => {
      // If we aren't playing anymore, do not advance
      if (playStateRef.current !== 'playing') return;

      currentChunkIndexRef.current++;
      if (currentChunkIndexRef.current < chunksRef.current.length) {
        timeoutRef.current = setTimeout(() => {
          if (playStateRef.current === 'playing') {
             playNextChunk();
          }
        }, 300);
      } else {
        updatePlayState('idle');
        // Driving Mode Logic when audio finishes
        if (isDrivingModeRef.current) {
          console.log("Driving Mode Prompt Triggered");
          setShowDrivingPrompt(true);

          const currentLessonIdx = enrollment.currentLessonIndex;
          const lessonKey = `${enrollment.currentLevel}-${currentLessonIdx}`;
          const currentLesson = currentLevelLessons[currentLessonIdx];

          setTimeout(() => {
            /* VISUAL INTELLIGENCE DISABLED FOR PILOT
            if (currentLesson?.visuals?.length > 0 && !hasPromptedVisuals[lessonKey]) {
              console.log("Driving Visual Prompt Triggered (End of Content 2)");
              setHasPromptedVisuals(prev => ({ ...prev, [lessonKey]: true }));
              currentPromptContextRef.current = 'visual';
              const message = `I noticed this lesson includes an important ${currentLesson.visuals[0].type}. Would you like me to explain it before we continue?`;
              speakText(message, () => {
                startVoiceCommandListener();
              });
            } else */ if (depth !== 'advanced') {
              currentPromptContextRef.current = 'quiz';
              speakText("Would you like to take a quick test, or move to the next lesson?", () => {
                startVoiceCommandListener();
              });
            } else {
              currentPromptContextRef.current = 'nextLesson';
              speakText("Would you like to take the final course assessment?", () => {
                startVoiceCommandListener();
              });
            }
          }, 500);

          // Auto-next timeout
          const timer = setTimeout(() => {
            if (!isDrivingModeRef.current) return;
            setShowDrivingPrompt(false);
            setIsWaitingForVoiceCommand(false);
            if (recognitionRef.current) recognitionRef.current.stop();
            
            console.log("User selected: Next Lesson (Timeout Auto-select)");
            if (checkIsLastLesson() && depth === 'advanced') {
              setShowCourseCompleteModal(true);
              if (enrollment) saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, true);
              speakText("Course completed.", () => {
                updatePlayState('idle');
              });
            } else {
              speakText("Moving to the next lesson automatically.", () => {
                autoPlayRef.current = true;
                moveToNextStage();
              });
            }
          }, 15000);
          promptTimeoutRef.current = timer;
        }
      }
    };

    utterance.onerror = (e) => {
      if (e.error !== 'canceled') {
        updatePlayState('idle');
      }
    };

    window.speechSynthesis.speak(utterance);
  };

  const handlePause = () => {
    if ('speechSynthesis' in window) {
      updatePlayState('paused');
      window.speechSynthesis.cancel();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  };

  const handlePlay = (text: string) => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech is not supported in this browser.');
      return;
    }

    if (playStateRef.current === 'paused') {
      updatePlayState('playing');
      const event = new CustomEvent('audioConflictStart', { detail: 'course' });
      window.dispatchEvent(event);

      window.speechSynthesis.cancel();
      playNextChunk(); 
      return;
    }

    const event = new CustomEvent('audioConflictStart', { detail: 'course' });
    window.dispatchEvent(event);

    window.speechSynthesis.cancel();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    const sanitized = cleanTextForSpeech(text);
    // Split by sentence terminators to provide natural utterance boundaries
    // We avoid commas because Web Speech handles commas correctly and chunking by comma makes it choppy
    chunksRef.current = sanitized.split(/[.?!;]+/).filter(c => c.trim().length > 0);
    currentChunkIndexRef.current = 0;
    
    updatePlayState('playing');
    
    if ('mediaSession' in navigator) {
      const lessonTitle = course ? course.title : 'Course Lesson';
      navigator.mediaSession.metadata = new MediaMetadata({
        title: lessonTitle,
        artist: 'Lumina Driving Mode',
        album: 'Lumina Learn'
      });
      navigator.mediaSession.setActionHandler('play', () => { handlePlay(text); });
    }
    
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        playNextChunk();
        window.speechSynthesis.onvoiceschanged = null;
      };
    } else {
      playNextChunk();
    }
  };

  // Listen for audio conflicts
  useEffect(() => {
    const handleConflict = (e: any) => {
      if (e.detail !== 'course' && playStateRef.current === 'playing') {
         updatePlayState('paused');
         window.speechSynthesis.cancel();
         if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    };
    window.addEventListener('audioConflictStart', handleConflict);
    return () => window.removeEventListener('audioConflictStart', handleConflict);
  }, []);

  const handleStop = () => {
    releaseDrivingWakeLock('handle_stop');
    if (promptTimeoutRef.current) {
      clearTimeout(promptTimeoutRef.current);
      promptTimeoutRef.current = null;
    }
    setShowDrivingPrompt(false);
    currentPromptContextRef.current = 'none';

    if ('speechSynthesis' in window) {
      updatePlayState('idle');
      window.speechSynthesis.cancel();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      currentChunkIndexRef.current = 0;
    }
    if (recognitionRef.current && isWaitingForVoiceCommand) {
      recognitionRef.current.stop();
      setIsWaitingForVoiceCommand(false);
    }
  };

  useEffect(() => {
    // Sync depth with enrollment's currentDepth if available, or keep current depth, but unlock properly
    if (enrollment?.currentDepth) {
      setDepth(enrollment.currentDepth as any);
    }
    const isFlexible = (course as any)?.progressionMode === 'flexible' || !(course as any)?.progressionMode;
    
    if (isFlexible) {
      // Keep everything unlocked
      setUnlockedDepths({ beginner: true, intermediate: true, advanced: true });
    } else {
      // In locked mode, jumping back to previous lessons should probably show it as unlocked if we've completed it.
      // But for now, we unlock based on enrollment's currentDepth.
      if (enrollment?.currentDepth === 'advanced') {
        setUnlockedDepths({ beginner: true, intermediate: true, advanced: true });
      } else if (enrollment?.currentDepth === 'intermediate') {
        setUnlockedDepths({ beginner: true, intermediate: true });
      } else {
        setUnlockedDepths({ beginner: true });
      }
    }
  }, [enrollment?.currentLessonIndex, enrollment?.currentLevel, course, enrollment?.currentDepth]);

  useEffect(() => {
    handleStop();
    
    if (!courseId || !enrollment || !course) return;
    
    // Automatically generate depths if they don't exist for the current lesson
    const generateDepthsIfNeeded = async () => {
      const levelData = course.levels?.[enrollment.currentLevel] || (course as any)?.[enrollment.currentLevel];
      const isOldFormat = Array.isArray(levelData);
      const currentLevelLessons = isOldFormat ? levelData : levelData?.lessons || [];
      const currentContent = currentLevelLessons[enrollment.currentLessonIndex];
      
      if (!currentContent) return;
      
      const hasPreGenerated = currentContent?.beginner?.audio || currentContent?.beginner?.content || (typeof currentContent === 'string');
      const depthKey = `${enrollment.currentLevel}_${enrollment.currentLessonIndex}`;

      if (!hasPreGenerated && !generatedDepths[depthKey] && !isGeneratingDepths) {
        setIsGeneratingDepths(true);
        try {
          const lessonTitle = currentContent?.title || course.title || 'this topic';
          
          const newDepths = await generateLessonDepths(currentContent);
          setGeneratedDepths(prev => ({ ...prev, [depthKey]: newDepths }));
        } catch (err) {
          console.error('Failed to generate depths automatically', err);
        } finally {
          setIsGeneratingDepths(false);
        }
      }
    };

    generateDepthsIfNeeded();
    
    return () => handleStop();
  }, [depth, enrollment?.currentLessonIndex, enrollment?.currentLevel, courseId, course]);

  // Auto-play logic for Driving Mode when switching levels
  useEffect(() => {
    if (isDrivingMode && autoPlayRef.current && !isGeneratingDepths && course && enrollment) {
      const levelData = course.levels?.[enrollment.currentLevel] || (course as any)?.[enrollment.currentLevel];
      const isOldFormat = Array.isArray(levelData);
      const currentLevelLessons = isOldFormat ? levelData : levelData?.lessons || [];
      const depthKey = `${enrollment.currentLevel}_${enrollment.currentLessonIndex}`;
      let content = currentLevelLessons[enrollment.currentLessonIndex];
      
      if (generatedDepths[depthKey]) {
        content = { ...content, ...generatedDepths[depthKey] };
      }

      const textToPlay = (content as any)?.[depth]?.audio || (typeof content === 'string' ? content : content?.beginner?.audio);
      
      if (textToPlay) {
        autoPlayRef.current = false;
        setTimeout(() => {
          handlePlay(textToPlay);
        }, 100);
      }
    }
  }, [depth, enrollment?.currentLessonIndex, enrollment?.currentLevel, isGeneratingDepths, course, generatedDepths, isDrivingMode]);

  useEffect(() => {
    if (!courseId) return;
    const fetchCourse = async () => {
      try {
        const docRef = doc(db, 'courses', courseId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCourse({ id: docSnap.id, ...docSnap.data() } as Course);
          Diagnostics.logStep(GoldenPathStep.LEARNER_OPEN, 'success', { courseId });
        } else {
          navigate('/');
        }
      } catch (error) {
        handleFirestoreError(error, OperationType.GET, `courses/${courseId}`);
      }
    };
    fetchCourse();
  }, [courseId, navigate]);

  useEffect(() => {
    if (!user || !courseId) return;
    const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
    const unsubscribe = onSnapshot(enrollmentRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as Enrollment;
        setEnrollment(data);
        if (data.currentDepth) {
          setDepth(data.currentDepth as 'beginner' | 'intermediate' | 'advanced');
          if (data.currentDepth === 'advanced') {
            setUnlockedDepths({ beginner: true, intermediate: true, advanced: true });
          } else if (data.currentDepth === 'intermediate') {
            setUnlockedDepths({ beginner: true, intermediate: true });
          } else {
            setUnlockedDepths({ beginner: true });
          }
        }
      } else {
        const initialEnrollment: Enrollment = {
          currentLevel: 'explorer',
          currentLessonIndex: 0,
          completed: false,
          currentDepth: 'beginner'
        };
        setDoc(enrollmentRef, {
          ...initialEnrollment,
          userId: user.uid,
          learnerId: user.uid,
          learnerEmail: user.email || 'anonymous',
          courseId: courseId,
          startedAt: serverTimestamp(),
          lastAccessed: serverTimestamp(),
          quizAttempts: 0,
          progressPercentage: 0,
          courseCompleted: false,
          finalAssessmentStatus: 'Not Started'
        }, { merge: true }).catch(err => handleFirestoreError(err, OperationType.CREATE, `enrollments/${user.uid}_${courseId}`));
        setEnrollment(initialEnrollment);
        setDepth('beginner');
        setUnlockedDepths({ beginner: true });
      }
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `enrollments/${user.uid}_${courseId}`);
    });
    return () => unsubscribe();
  }, [user, courseId]);

  useEffect(() => {
    if (!courseId) return;
    const q = query(collection(db, 'comments'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const courseComments = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Comment))
        .filter(c => (c as any).courseId === courseId);
      setComments(courseComments);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'comments');
    });
    return () => unsubscribe();
  }, [courseId]);

  useEffect(() => {
    // chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (user && courseId) {
      const q = query(collection(db, 'user_concept_mastery'), where('userId', '==', user.uid), where('courseId', '==', courseId));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const reviews = snapshot.docs
          .map(doc => doc.data())
          .filter(d => d.nextReviewAt && d.nextReviewAt.toDate() <= new Date());
        setDueReviews(reviews);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'user_concept_mastery');
      });
      return () => unsubscribe();
    }
  }, [user, courseId]);

  useEffect(() => {
    if (user) {
      const unsubscribe = onSnapshot(doc(db, 'user_learning_profiles', user.uid), (doc) => {
        if (doc.exists()) setGlobalProfile(doc.data());
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, `user_learning_profiles/${user.uid}`);
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (user && courseId && enrollment) {
      const q = query(
        collection(db, 'user_learning_objectives'), 
        where('userId', '==', user.uid), 
        where('courseId', '==', courseId),
        where('lessonId', '==', `${enrollment.currentLevel}_${enrollment.currentLessonIndex}`)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const objectives = snapshot.docs.map(doc => doc.data());
        setLearningObjectives(objectives);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'user_learning_objectives');
      });
      return () => unsubscribe();
    }
  }, [user, courseId, enrollment]);

  const getFlatSequence = () => {
    if (!course) return [];
    const seq: Array<{ depth: 'beginner' | 'intermediate' | 'advanced', module: string, idx: number, _lesson: any }> = [];
    const depthsArr = ['beginner', 'intermediate', 'advanced'] as const;
    const modulesArr = ['explorer', 'apprentice', 'master'] as const;
    
    depthsArr.forEach(d => {
        modulesArr.forEach(m => {
            const lData = course.levels?.[m];
            const lessons = Array.isArray(lData) ? lData : (lData?.lessons || []);
            lessons.forEach((lessonContent: any, idx: number) => {
                seq.push({ depth: d, module: m, idx, _lesson: lessonContent });
            });
        });
    });
    return seq;
  };

  const flatSeq = getFlatSequence();

  const getFurthestIndex = () => {
    if (!course || !enrollment) return 0;
    let furthest = 0;
    for (let i = 0; i < flatSeq.length; i++) {
       const s = flatSeq[i];
       const id = `${s.module}_${s.idx}_${s.depth}`;
       const c1 = enrollment.knowledgeCheckCompletedLessonIds?.includes(id);
       const c2 = enrollment.skippedKnowledgeCheckLessonIds?.includes(id);
       if (!c1 && !c2) {
           break;
       }
       furthest = i + 1;
    }
    return Math.min(furthest, flatSeq.length - 1 < 0 ? 0 : flatSeq.length - 1);
  };

  const furthestIndex = getFurthestIndex();

  const isDepthUnlocked = (d: string) => {
    if (d === 'beginner') return true;
    const firstOfDepthIdx = flatSeq.findIndex(s => s.depth === d);
    return firstOfDepthIdx !== -1 && firstOfDepthIdx <= furthestIndex;
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !courseId || !newComment.trim()) return;
    try {
      await addDoc(collection(db, 'comments'), {
        courseId,
        userId: user.uid,
        userName: user.displayName || 'Anonymous Student',
        text: newComment.trim(),
        createdAt: serverTimestamp()
      });
      await setDoc(doc(db, 'courses', courseId), { commentsCount: increment(1) }, { merge: true });
      setNewComment('');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'comments');
    }
  };

  const saveProgress = async (level: string, index: number, completed: boolean = false, extraData: any = {}) => {
    if (!user || !courseId) return;
    setSaving(true);
    Diagnostics.logStep(GoldenPathStep.PROGRESS_SAVED, 'start', { level, index, completed });
    try {
      const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
      
      const updateData: any = {
        currentLevel: level,
        currentLessonIndex: index,
        currentDepth: extraData.currentDepth || depth,
        completed,
        lastAccessed: serverTimestamp(),
        userId: user.uid,
        learnerId: user.uid,
        learnerName: user.displayName || 'Anonymous Learner',
        learnerEmail: user.email || 'anonymous',
        courseId: courseId,
        courseTitle: course?.title || 'Unknown Course',
        tutorId: (course as any)?.tutorId || (course as any)?.ownerUserId || null,
        organisationId: course?.orgId || null,
        updatedAt: serverTimestamp(),
        ...extraData
      };

      if (course && course.levels) {
        updateData.totalLessons = flatSeq.length;
        
        let targetDepth = extraData.currentDepth || depth;
        let comp = flatSeq.findIndex(s => s.module === level && s.idx === index && s.depth === targetDepth);
        if (comp === -1) comp = furthestIndex;
        else if (completed) comp += 1;
        
        updateData.completedLessons = Math.max(0, Math.min(flatSeq.length, comp));
        updateData.progressPercentage = flatSeq.length > 0 ? Math.round((updateData.completedLessons / flatSeq.length) * 100) : 0;
        
        // Track the current lesson as viewed 
        const currentLessonId = `${level}_${index}`;
        updateData.viewedLessonIds = arrayUnion(currentLessonId);

        // If completed, track it as completed
        if (completed || (enrollment?.completed && enrollment?.currentLevel === level && enrollment?.currentLessonIndex === index)) {
          updateData.completedLessonIds = arrayUnion(currentLessonId);
        }

        if (completed && targetDepth === 'advanced' && updateData.progressPercentage >= 100) {
          updateData.completedAt = serverTimestamp();
          updateData.courseCompleted = true;
          updateData.finalAssessmentStatus = 'Passed';
        }
      }

      await setDoc(enrollmentRef, updateData, { merge: true });
      setIsQuizModalOpen(false);
      resetTempVisualQuiz();
      setQuizAnswers([]);
      setQuizError('');
      Diagnostics.logStep(GoldenPathStep.PROGRESS_SAVED, 'success', { level, index, completed });
    } catch (error: any) {
      Diagnostics.logStep(GoldenPathStep.PROGRESS_SAVED, 'fail', { error: error.message });
      handleFirestoreError(error, OperationType.UPDATE, `enrollments/${user.uid}_${courseId}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePauseAndExit = async () => {
    if (!enrollment) return;
    await saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, enrollment.completed);
    navigate('/');
  };

  const handleMarkAsComplete = async () => {
    if (!course || !enrollment) return;
    
    const currentSeqIdx = flatSeq.findIndex(s => s.module === enrollment.currentLevel && s.idx === enrollment.currentLessonIndex && s.depth === depth);
    if (currentSeqIdx === -1) return;

    if (currentSeqIdx < flatSeq.length - 1) {
      const nextItem = flatSeq[currentSeqIdx + 1];
      if (nextItem.depth !== depth) {
        setDepth(nextItem.depth);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      await saveProgress(nextItem.module, nextItem.idx, false, { currentDepth: nextItem.depth });
    } else {
      await saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, true, { currentDepth: depth });
      setShowCourseCompleteModal(true);
    }
  };

  const handlePrev = () => {
    if (!course || !enrollment) return;
    const currentSeqIdx = flatSeq.findIndex(s => s.module === enrollment.currentLevel && s.idx === enrollment.currentLessonIndex && s.depth === depth);
    if (currentSeqIdx > 0) {
      const prevItem = flatSeq[currentSeqIdx - 1];
      if (prevItem.depth !== depth) {
        setDepth(prevItem.depth);
      }
      saveProgress(prevItem.module, prevItem.idx, false, { currentDepth: prevItem.depth });
    }
  };

  console.log("[CoursePlayer] Rendering", { 
    courseId, 
    userId: user?.uid, 
    level: enrollment?.currentLevel, 
    lessonIndex: enrollment?.currentLessonIndex,
    hasCourse: !!course,
    hasEnrollment: !!enrollment
  });

  // ALL logic and variable definitions must be after hooks but before returns.
  
  const levelKey = enrollment?.currentLevel || 'explorer';
  const lData = course?.levels?.[levelKey] || (course as any)?.[levelKey];
  const isOld = Array.isArray(lData);
  const currentLevelLessons = isOld ? lData : lData?.lessons || [];
  const currentLessonStructure = currentLevelLessons?.[enrollment?.currentLessonIndex || 0];
  const isFlexible = (course as any)?.progressionMode === 'flexible' || !(course as any)?.progressionMode;
  
  const getNextDestinationInfo = () => {
    if (!enrollment || !course) return { label: 'Complete Course', type: 'complete' };
    const currentSeqIdx = flatSeq.findIndex(s => s.module === enrollment.currentLevel && s.idx === enrollment.currentLessonIndex && s.depth === depth);
    
    if (currentSeqIdx < flatSeq.length - 1) {
      const nextItem = flatSeq[currentSeqIdx + 1];
      if (nextItem.depth !== depth) {
        return { label: `Next: ${nextItem.depth.charAt(0).toUpperCase() + nextItem.depth.slice(1)} Level`, type: 'level' };
      } else if (nextItem.module !== enrollment.currentLevel) {
        const order = ['explorer', 'apprentice', 'master'];
        return { label: `Next: Module ${order.indexOf(nextItem.module) + 1}`, type: 'module' };
      } else {
        const title = nextItem._lesson?.title || nextItem._lesson?.lesson_title || `Lesson ${nextItem.idx + 1}`;
        return { label: `Next: ${title}`, type: 'chapter' };
      }
    } else {
      return { label: 'Complete Course', type: 'complete' };
    }
  };

  const nextDest = getNextDestinationInfo();
  
  // Depth Data Management
  const lessonData = currentLessonStructure as any;
  const hasPreGenerated = lessonData?.[depth]?.audio || lessonData?.[depth]?.content;
  
  let currentContent = currentLessonStructure;
  if (hasPreGenerated) {
    currentContent = { 
      ...currentContent, 
      ...lessonData[depth],
      visuals: lessonData.visuals || []
    };
  } else if (enrollment && generatedDepths[`${enrollment.currentLevel}_${enrollment.currentLessonIndex}`]) {
    const depthData = generatedDepths[`${enrollment.currentLevel}_${enrollment.currentLessonIndex}`];
    currentContent = { 
        ...currentContent, 
        ...depthData,
        visuals: (depthData.visuals && depthData.visuals.length > 0) ? depthData.visuals : (currentContent as any).visuals
    };
  }

  const handleGenerateLessonDepth = async (idx: number, content: any, overrideLevel?: string) => {
    if (!content) {
      console.warn("[CoursePlayer] Cannot generate depth: content is missing");
      return;
    }
    const currentLevelForGen = overrideLevel || enrollment?.currentLevel || 'explorer';
    const depthKey = `${currentLevelForGen}_${idx}`;
    setIsGeneratingDepths(true);
    setGenerationFailedIdxs(prev => ({ ...prev, [depthKey]: false }));
    try {
      const newDepths = await generateLessonDepths(content);
      setGeneratedDepths(prev => ({ ...prev, [depthKey]: newDepths }));
      // Also clear cached quizzes to prevent stale questions after regenerating
      if (courseId && enrollment) {
         setGeneratedQuizzes(prev => {
            const next = { ...prev };
            delete next[`${courseId}-${currentLevelForGen}-${idx}-beginner`];
            delete next[`${courseId}-${currentLevelForGen}-${idx}-intermediate`];
            delete next[`${courseId}-${currentLevelForGen}-${idx}-advanced`];
            return next;
         });
      }
    } catch (err) {
      console.error('Failed to auto-generate depths', err);
      setGenerationFailedIdxs(prev => ({ ...prev, [depthKey]: true }));
    } finally {
      setIsGeneratingDepths(false);
    }
  };

  // Auto-generate depth content if missing (if pre-generation failed or for old courses)
  useEffect(() => {
    if (!enrollment) return;
    const hasCurrentDepthContent = (currentContent as any)?.[depth]?.audio;
    if (currentContent && !hasCurrentDepthContent && !isGeneratingDepths && typeof currentContent !== 'string') {
      const idx = enrollment.currentLessonIndex;
      const depthKey = `${enrollment.currentLevel}_${idx}`;
      if (!generatedDepths[depthKey] && !generationFailedIdxs[depthKey] && !hasPreGenerated) {
        console.log("[CoursePlayer] Auto-generating depth content for lesson", idx);
        handleGenerateLessonDepth(idx, currentContent, enrollment.currentLevel);
      }
    }
  }, [currentContent, depth, enrollment?.currentLessonIndex, enrollment?.currentLevel, isGeneratingDepths, generatedDepths, generationFailedIdxs, hasPreGenerated]);

  const lessonTitle = typeof currentLessonStructure === 'string' 
    ? currentLessonStructure 
    : (currentLessonStructure?.title || currentLessonStructure?.lesson_title || course?.title || 'this topic');
    
  const baseAudio = typeof currentContent === 'string' ? currentContent : (currentContent?.audio || currentContent?.beginner?.audio || '');

  const currentQuizKey = `${courseId}-${enrollment?.currentLevel}-${enrollment?.currentLessonIndex || 0}-${depth}`;
  const currentQuiz = generatedQuizzes["TEMP_VISUAL"] || generatedQuizzes[currentQuizKey] || [];
  
  const resetTempVisualQuiz = () => {
    setGeneratedQuizzes(prev => {
      const next = { ...prev };
      delete next["TEMP_VISUAL"];
      return next;
    });
  };
  
  const isFirstLesson = enrollment?.currentLevel === 'explorer' && enrollment?.currentLessonIndex === 0;
  const isLastLesson = enrollment?.currentLevel === 'master' && enrollment?.currentLessonIndex === Math.max(0, currentLevelLessons.length - 1);

  const levelOrder = { explorer: 0, apprentice: 1, master: 2 };

  // Calculate quiz performance
  const quizPerformance = currentQuiz.map((q: any, i: number) => ({
    question: q.question,
    isCorrect: quizAnswers[i] === q.correctAnswerIndex,
    answered: quizAnswers[i] !== -1
  })).filter(p => p.answered);

  // --------------------------------------------------------------------------------
  // RENDER BLOCKS
  // --------------------------------------------------------------------------------

  // Move evaluation of loading state here but return later to ensure all hooks run
  const isLoaded = !loading && course && enrollment;

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main text-text-alt">
      {/* Diagnostics Overlay (Shift+D to toggle) */}
      {showDiag && (
          <div className="fixed top-20 right-4 z-[9999] bg-slate-900/90 text-slate-100 p-6 rounded-2xl border border-slate-700 shadow-2xl backdrop-blur-xl w-96 overflow-y-auto max-h-[80vh] font-mono text-xs">
            <div className="flex justify-between items-center mb-4">
               <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                <Settings className="w-4 h-4" /> COURSE DIAGNOSTICS
              </h3>
              <button onClick={() => setShowDiag(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <section>
                <h4 className="text-indigo-400 font-bold mb-1">Source Info</h4>
                <div className="bg-black/30 p-2 rounded border border-white/5 space-y-1">
                  <p>Course ID: {courseId}</p>
                  <p>Sources: {course?.sources?.pdfs?.length || 0} PDFs, {course?.sources?.urls?.length || 0} URLs</p>
                  {course?.sources?.pdfs?.map((p: any) => (
                    <p key={p.id} className="opacity-60 text-[10px] break-all">PDF: {p.name} ({p.id})</p>
                  ))}
                </div>
              </section>

              <section>
                <h4 className="text-indigo-400 font-bold mb-1">Visual Intelligence</h4>
                <div className="bg-black/30 p-2 rounded border border-white/5 space-y-1">
                  <p>Current Lesson Visuals: {(currentContent as any)?.visuals?.length || 0}</p>
                  {(currentContent as any)?.visuals?.map((v: any, i: number) => (
                    <div key={i} className="mt-2 pt-2 border-t border-white/5">
                      <p className="text-emerald-400">{v.type} (Page {v.page_number})</p>
                      <p className="opacity-60 text-[10px] italic">{v.title}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h4 className="text-indigo-400 font-bold mb-1">Active Tutor Context</h4>
                <div className="bg-black/30 p-2 rounded border border-white/5 text-[10px]">
                  <p className="font-bold">Lesson Content Length: {String(((currentContent as any)?.[depth]?.audio || currentContent || '').length)} chars</p>
                </div>
              </section>

              <div className="pt-4 flex justify-between text-[8px] opacity-40">
                <span>Shift+D to toggle</span>
              </div>
            </div>
          </div>
      )}
      {/* Learning Status Banner */}
      <div className="bg-bg-alt/50 border-b border-border-main px-6 py-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider flex-wrap gap-2">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-indigo-500">
            <Zap className="w-3.5 h-3.5" />
            <span>Learning Momentum: {globalProfile?.momentum?.score || 0}%</span>
          </div>
          <div className="flex items-center gap-2 text-emerald-500">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Avg Mastery: {Math.round(globalProfile?.averageMasteryScore || 0)}%</span>
          </div>

          <div className="flex items-center gap-2 text-amber-500 bg-amber-500/5 px-2 py-0.5 rounded border border-amber-500/10">
            <AlertCircle className="w-3.5 h-3.5" />
            <span className="capitalize normal-case text-xs">You are using a pilot course. Please use the feedback button if anything does not behave as expected.</span>
          </div>

          {/* Partial Success Warning */}
          {course?.generationMetadata?.isPartial && (
            <div className="flex items-center gap-2 text-amber-500 bg-amber-500/5 px-3 py-1 rounded-full border border-amber-500/10 animate-in fade-in slide-in-from-left-2 duration-700">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Partial Data Mode: Some sections may be incomplete</span>
            </div>
          )}
          
          {/* Momentum Nudge */}
          {globalProfile?.momentum?.score > 80 && (
            <div className="flex items-center gap-2 text-amber-500 animate-pulse">
              <Zap className="w-3.5 h-3.5" />
              <span>You're on fire! Consider trying the Advanced depth.</span>
            </div>
          )}
          {globalProfile?.momentum?.score < 30 && globalProfile?.momentum?.score > 0 && (
            <div className="flex items-center gap-2 text-rose-500">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Low momentum. Try a quick review to get back on track!</span>
            </div>
          )}
        </div>
        {dueReviews.length > 0 && (
          <button 
            onClick={() => setIsReviewMode(true)}
            className="flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-500 rounded-full border border-amber-500/20 hover:bg-amber-500/20 transition-all animate-pulse"
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{dueReviews.length} Reviews Due</span>
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-6rem)] bg-bg-main relative">
      {/* Sidebar: 3-Level Path */}
      <div className="w-full lg:w-72 bg-bg-alt border-b lg:border-b-0 lg:border-r border-border-main p-6 shrink-0 lg:overflow-y-auto">
        <div className="mb-8">
            <h2 className="text-xl font-bold text-text-main">Course Path</h2>
            <p className="text-xs text-indigo-400 font-semibold uppercase tracking-wider mt-1">
                Viewing {depth} depth
            </p>
        </div>
        <div className="space-y-8">
          {(['explorer', 'apprentice', 'master'] as const).map((level, idxLevel) => {
            const lData = course.levels?.[level];
            const lLessons = Array.isArray(lData) ? lData : lData?.lessons || [];
            if (lLessons.length === 0) return null;
            return (
              <div key={level}>
                <h3 className="text-sm font-bold text-indigo-500 uppercase tracking-wider mb-4">Module {idxLevel + 1}</h3>
                <div className="space-y-3">
                  {lLessons.map((_: any, idx: number) => {
                    const isCurrent = enrollment.currentLevel === level && enrollment.currentLessonIndex === idx;
                    const seqIndex = flatSeq.findIndex(s => s.module === level && s.idx === idx && s.depth === depth);
                    const isFlexible = (course as any)?.progressionMode === 'flexible' || !(course as any)?.progressionMode;
                    const isUnlocked = isFlexible || enrollment?.completed || (seqIndex !== -1 && seqIndex <= furthestIndex);
                    const isCheckmarked = enrollment?.completed || enrollment?.knowledgeCheckCompletedLessonIds?.includes(`${level}_${idx}_advanced`);

                    return (
                      <button 
                        key={idx} 
                        onClick={async () => {
                           if (isUnlocked) {
                              setShowCourseCompleteModal(false);
                              setIsQuizModalOpen(false);
                              handleStop();
                              await saveProgress(enrollment.currentLevel, enrollment.currentLessonIndex, false);
                              
                              // Create an optimistic local update 
                              setEnrollment(prev => prev ? {
                                 ...prev,
                                 currentLevel: level,
                                 currentLessonIndex: idx,
                                 currentDepth: depth
                              } : prev);
                              
                              // We also update DB
                              if (user && courseId) {
                                  try {
                                      const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
                                      await setDoc(enrollmentRef, {
                                          currentLevel: level,
                                          currentLessonIndex: idx,
                                      }, { merge: true });
                                  } catch (e) {
                                      console.error("Failed to jump", e);
                                  }
                              }
                           }
                        }}
                        disabled={!isUnlocked}
                        className={`flex items-center gap-3 w-full text-left text-sm ${isCurrent ? 'text-text-main font-medium' : isCheckmarked ? 'text-emerald-500' : 'text-text-alt opacity-60'} ${isUnlocked && !isCurrent ? 'cursor-pointer hover:text-text-main hover:opacity-100 transition-colors' : ''} ${!isUnlocked ? 'cursor-not-allowed' : ''}`}
                      >
                        {isCheckmarked ? (
                          <CheckCircle className="h-5 w-5 shrink-0" />
                        ) : (
                          <div className={`h-5 w-5 shrink-0 rounded-full border-2 ${isCurrent ? 'border-indigo-500 bg-indigo-500/20' : 'border-border-main'}`} />
                        )}
                        <span className="line-clamp-1">{_.lesson_title || `Lesson ${idx + 1}`}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto pb-32">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-main tracking-tight">{course.title}</h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-text-alt">
              <span className="uppercase tracking-wider font-semibold text-indigo-500">
                {enrollment.currentLevel === 'explorer' ? 'Module 1' : enrollment.currentLevel === 'apprentice' ? 'Module 2' : 'Module 3'}
              </span>
              <span>•</span>
              <span>Lesson {enrollment.currentLessonIndex + 1} of {currentLevelLessons.length}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-text-alt bg-bg-alt px-4 py-2 rounded-xl border border-border-main">
              {saving ? <Loader2 className="h-4 w-4 animate-spin text-indigo-500" /> : <Save className="h-4 w-4 text-emerald-500" />}
              <span className="hidden sm:inline">{saving ? 'Saving...' : 'Saved'}</span>
            </div>
            <button onClick={handlePauseAndExit} className="flex items-center gap-2 px-4 py-2 bg-bg-alt hover:bg-bg-alt/80 text-text-main rounded-xl transition-colors text-sm font-medium border border-border-main">
              <LogOut className="h-4 w-4" />
              Pause & Exit
            </button>
          </div>
        </div>

        <div className="bg-bg-alt rounded-2xl shadow-2xl border border-border-main overflow-hidden mb-12">
          {/* Depth Toggles */}
          {currentContent && (
            <div className="flex border-b border-border-main bg-bg-main/50 px-6 pt-4 gap-2">
              {(['beginner', 'intermediate', 'advanced'] as const).map(d => (
                <button
                  key={d}
                  onClick={() => {
                        setDepth(d);
                        if (user && courseId && enrollment) {
                           setEnrollment(prev => prev ? { ...prev, currentDepth: d } : prev);
                           const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
                           setDoc(enrollmentRef, { currentDepth: d }, { merge: true });
                        }
                  }}
                  disabled={!isDepthUnlocked(d) && !isFlexible}
                  className={`px-6 py-3 rounded-t-lg text-sm font-medium capitalize transition-colors ${depth === d ? 'bg-bg-alt text-indigo-500 border-t border-x border-border-main -mb-[1px]' : 'text-text-alt hover:text-text-main hover:bg-bg-alt/50 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                >
                  {d}
                </button>
              ))}
            </div>
          )}

          <div className="p-8">
            {(!currentLevelLessons || currentLevelLessons.length === 0) ? (
              <div className="text-center py-20 bg-bg-main/50 rounded-xl border border-border-main">
                 <AlertCircle className="mx-auto h-12 w-12 text-text-alt opacity-20 mb-4" />
                 <h3 className="text-xl font-bold text-text-main mb-2" id="no-content-title">No Content Found</h3>
                 <p className="text-text-alt max-w-md mx-auto">This level doesn't seem to have any lessons yet. Please check back later or try another level.</p>
              </div>
            ) : (
            <div className="grid grid-cols-1 gap-8">
              {/* Audio Column (100%) */}
              <div className="bg-bg-main/50 rounded-xl p-4 sm:p-6 border border-border-main w-full overflow-hidden" id="audio-column">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-2 text-emerald-500 font-medium text-lg">
                    <Volume2 className="h-6 w-6 shrink-0" />
                    [Audio Script]
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                    {!isMobileDevice() && (
                      <button
                        onClick={async () => {
                          setIsGeneratingDepths(true);
                          try {
                            const newDepths = await generateLessonDepths(currentContent as any);
                            setGeneratedDepths(prev => ({ ...prev, [enrollment.currentLessonIndex]: newDepths }));
                          } catch (err) {
                            console.error('Failed to generate depths', err);
                          } finally {
                            setIsGeneratingDepths(false);
                          }
                        }}
                        disabled={isGeneratingDepths}
                        className="text-xs px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-500 rounded-lg transition-colors border border-indigo-500/30 disabled:opacity-50"
                      >
                        {isGeneratingDepths ? 'Generating...' : 'Regenerate Content'}
                      </button>
                    )}

                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <select
                        value={selectedVoiceURI}
                        onChange={(e) => {
                          setSelectedVoiceURI(e.target.value);
                          localStorage.setItem('lumina_preferred_voice', e.target.value);
                        }}
                        className="bg-bg-alt text-text-alt text-sm rounded-lg border border-border-main px-3 py-2 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 w-full sm:w-[250px] truncate"
                      >
                        {voices.filter(v => !v.name.toLowerCase().includes('bells') && !v.name.toLowerCase().includes('boing') && !v.name.toLowerCase().includes('cellos') && !v.name.toLowerCase().includes('whisper') && !v.name.toLowerCase().includes('zarvox') && !v.name.toLowerCase().includes('trinoids')).sort((a,b) => {
                          const aRec = a.name.includes('Google') || a.name.includes('Samantha') || a.name.includes('Daniel');
                          const bRec = b.name.includes('Google') || b.name.includes('Samantha') || b.name.includes('Daniel');
                          if (aRec && !bRec) return -1;
                          if (!aRec && bRec) return 1;
                          return 0;
                        }).map(voice => {
                          const isRecommended = voice.name.includes('Google') || voice.name.includes('Samantha') || voice.name.includes('Daniel') || voice.name.includes('Natural');
                          return (
                            <option key={voice.voiceURI} value={voice.voiceURI} className="bg-bg-main text-text-main">
                              {voice.name} {isRecommended ? '⭐ Recommended' : ''} ({voice.lang})
                            </option>
                          );
                        })}
                      </select>
                      {isDrivingMode && (
                        <span className="text-[10px] text-indigo-400/80 italic max-w-[250px] leading-tight">
                          Voice quality depends on your browser and device. More natural voices are coming soon.
                        </span>
                      )}
                    </div>

                    {/* Pro Audio Controls */}
                    <div className="flex items-center gap-2 bg-bg-alt p-1.5 rounded-lg border border-border-main" id="audio-controls-container">
                      <div className="relative">
                        <button
                          onClick={async () => {
                            const newValue = !isDrivingMode;
                            setIsDrivingMode(newValue);
                            localStorage.setItem('lumina_driving_mode', String(newValue));
                            localStorage.setItem('lumina_tooltip_driving', 'true');
                            window.dispatchEvent(new CustomEvent('drivingModeChanged', { detail: newValue }));
                            
                            if (newValue) {
                              console.log('[DrivingMode] driving_mode_enabled');
                                await requestDrivingWakeLock('driving_mode_enabled');
                                
                              // Audio Session Trick
                              try {
                                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                                if (AudioContext && !audioContextRef.current) {
                                  audioContextRef.current = new AudioContext();
                                  const osc = audioContextRef.current.createOscillator();
                                  const gain = audioContextRef.current.createGain();
                                  gain.gain.value = 0.0001; // almost silent
                                  osc.connect(gain);
                                  gain.connect(audioContextRef.current.destination);
                                  osc.start();
                                  dummyOscillatorRef.current = osc;
                                }
                              } catch(e) {}
                              speakText("Driving Mode enabled. I'll guide you through the course hands-free.");
                            } else {
                                await releaseDrivingWakeLock('driving_mode_disabled');
                              if (dummyOscillatorRef.current) { try { dummyOscillatorRef.current.stop() } catch(e){} dummyOscillatorRef.current = null; }
                              if (audioContextRef.current) { try { audioContextRef.current.close() } catch(e){} audioContextRef.current = null; }
                              speakText("Driving Mode disabled.");
                              handleStop();
                            }
                          }}
                          className={`p-2 rounded-md transition-all flex items-center gap-2 relative z-10 ${
                            isDrivingMode 
                              ? 'bg-indigo-500/20 text-indigo-500 border border-indigo-500/30' 
                              : 'text-text-alt hover:text-indigo-500 border border-transparent'
                          }`}
                          title="Toggle Driving Mode"
                        >
                          <Zap className={`h-4 w-4 ${isDrivingMode ? 'fill-indigo-500' : ''}`} />
                          <span className="text-[10px] font-bold uppercase tracking-wider hidden md:inline">Driving Mode</span>
                        </button>
                        
                        {!localStorage.getItem('lumina_tooltip_driving') && role === 'learner' && !isDrivingMode && (
                          <div className="absolute top-14 left-0 w-48 bg-indigo-600 text-white text-xs p-3 rounded-lg shadow-xl shadow-indigo-500/20 z-50 border border-indigo-500/50">
                            <div className="absolute -top-1.5 left-4 w-3 h-3 bg-indigo-600 rotate-45 border-l border-t border-indigo-500/50"></div>
                            <div className="flex justify-between items-start gap-2">
                              <span className="font-medium">Enable Driving Mode for hands-free learning.</span>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  localStorage.setItem('lumina_tooltip_driving', 'true');
                                  // force render update
                                  setIsDrivingMode(false); 
                                }}
                                className="text-white/70 hover:text-white"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-[1px] h-4 bg-border-main mx-1"></div>

                      <button
                        id="play-button"
                        onClick={() => handlePlay((currentContent as any)?.[depth]?.audio || '')}
                        disabled={isGeneratingDepths}
                        className={`p-2 rounded-md transition-colors ${
                          playState === 'playing' 
                            ? 'bg-emerald-500/20 text-emerald-500' 
                            : 'text-text-alt hover:text-emerald-500 hover:bg-bg-main disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                        }`}
                        title={playState === 'paused' ? 'Resume' : 'Play'}
                      >
                        <Play className="h-5 w-5" fill={playState === 'playing' ? "currentColor" : "none"} />
                      </button>
                      <button
                        id="pause-button"
                        onClick={handlePause}
                        disabled={playState === 'idle'}
                        className={`p-2 rounded-md transition-colors ${
                          playState === 'paused'
                            ? 'bg-amber-500/20 text-amber-500'
                            : 'text-text-alt hover:text-amber-500 hover:bg-bg-main disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                        }`}
                        title="Pause"
                      >
                        <Pause className="h-5 w-5" fill={playState === 'paused' ? "currentColor" : "none"} />
                      </button>
                      <button
                        id="stop-button"
                        onClick={handleStop}
                        disabled={playState === 'idle'}
                        className="p-2 rounded-md transition-colors text-text-alt hover:text-rose-500 hover:bg-bg-main disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title="Stop"
                      >
                        <Square className="h-5 w-5" fill="currentColor" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {isGeneratingDepths && (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
                      <span className="text-text-alt opacity-70">Generating comprehensive lesson content...</span>
                    </div>
                  </div>
                )}
                
                {generationFailedIdxs[`${enrollment.currentLevel}_${enrollment.currentLessonIndex}`] && !isGeneratingDepths && (
                  <div className="flex flex-col items-center justify-center py-12 px-6 bg-bg-main/50 border border-border-main rounded-2xl">
                    <AlertCircle className="h-10 w-10 text-rose-500 mb-4" />
                    <h3 className="text-lg font-semibold text-text-main">Could not generate lesson</h3>
                    <p className="text-text-alt text-center mt-2 max-w-sm">
                      Our factory encountered an error while synthesizing this section. This can happen with very technical or complex original documents.
                    </p>
                    <button
                      onClick={() => handleGenerateLessonDepth(enrollment.currentLessonIndex, currentContent, enrollment.currentLevel)}
                      className="mt-6 flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/20"
                    >
                      <Zap className="h-4 w-4" />
                      Retry Generation
                    </button>
                  </div>
                )}
                
                {!isGeneratingDepths && !generationFailedIdxs[`${enrollment.currentLevel}_${enrollment.currentLessonIndex}`] && (
                  <div className="prose prose-theme max-w-none text-text-alt text-base sm:text-lg leading-relaxed break-words" id="audio-script-content" data-clarity-mask="true">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{(currentContent as any)?.[depth]?.audio || '*No audio script available.*'}</Markdown>
                  </div>
                )}

                {/* Visual Insights Section - Disabled for MVP */}
                {/* {currentContent && (currentContent as any).visuals && (currentContent as any).visuals.length > 0 && (
                  <div className="mt-12 space-y-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Layout className="h-5 w-5 text-emerald-500" />
                      <h3 className="text-lg font-bold text-text-main uppercase tracking-wider">Visual Insights</h3>
                    </div>
                    
                    <div className="grid gap-6 sm:grid-cols-2">
                      {toArray<VisualAsset>((currentContent as any)?.visuals).map((visual: VisualAsset, vidx: number) => {
                        const doc = getDocument(visual.source_doc_id);
                        return (
                          <div key={visual.id || vidx} className="bg-bg-main border border-border-main rounded-2xl overflow-hidden flex flex-col shadow-2xl transition-transform hover:scale-[1.01] active:scale-[0.99]">
                            <div className="aspect-video bg-bg-alt flex items-center justify-center relative group">
                              {doc?.data ? (
                                <PageRenderer 
                                  pdfData={doc.data} 
                                  pageNumber={visual.page_number} 
                                  className="w-full h-full"
                                />
                              ) : visual.image_data ? (
                                <img 
                                  src={`data:image/png;base64,${visual.image_data}`} 
                                  alt={visual.type} 
                                  className="w-full h-full object-contain"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="flex flex-col items-center gap-2 text-text-alt opacity-20">
                                  <ImageIcon className="h-10 w-10" />
                                  <span className="text-[10px] font-mono tracking-widest uppercase">Page {visual.page_number}</span>
                                </div>
                              )}
                              
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <button className="p-3 bg-white/10 backdrop-blur-md rounded-full text-white">
                                  <Maximize2 className="h-5 w-5" />
                                </button>
                              </div>

                              <div className="absolute top-3 right-3 px-2 py-1 bg-bg-main/80 backdrop-blur-md rounded-lg text-[10px] font-black text-emerald-500 border border-emerald-500/30 uppercase tracking-tighter">
                                {visual.type}
                              </div>
                            </div>
                            
                            <div className="p-5 flex-1 flex flex-col bg-bg-alt">
                              <div className="mb-4">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Brain className="h-3.5 w-3.5 text-indigo-500" />
                                  <p className="text-[10px] text-text-alt font-bold uppercase tracking-widest">AI Interpretation</p>
                                </div>
                                <p className="text-sm text-text-main leading-relaxed font-medium">
                                  {visual.interpretation?.summary || "Analyzing visual components..."}
                                </p>
                              </div>

                              <div className="mt-auto grid grid-cols-2 gap-3">
                                <button
                                  onClick={() => {
                                    const message = `Explain the ${visual.type} on page ${visual.page_number} of the source document: ${visual.interpretation?.teaching_explanation || visual.interpretation?.summary}`;
                                    window.dispatchEvent(new CustomEvent('askTutor', { detail: { message } }));
                                  }}
                                  className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-bold text-emerald-500 rounded-xl border border-emerald-500/20 transition-all active:scale-95"
                                >
                                  <Zap className="h-3.5 w-3.5 fill-emerald-500" />
                                  Explain
                                </button>
                                <button
                                  onClick={async () => {
                                    setIsQuizModalOpen(true);
                                    setIsGeneratingQuiz(true);
                                    setQuizError('');
                                    try {
                                      const specializedQuiz = await generateQuizForLesson(
                                        `Focus on this visual interpretation: ${visual.interpretation?.teaching_explanation}`, 
                                        [visual]
                                      );
                                      setQuizAnswers(new Array(specializedQuiz.length).fill(-1));
                                      (window as any)._tempVisualQuiz = specializedQuiz;
                                      setGeneratedQuizzes(prev => ({ ...prev, "TEMP_VISUAL": specializedQuiz }));
                                    } catch (err) {
                                      setQuizError('Failed to generate visual quiz.');
                                    } finally {
                                      setIsGeneratingQuiz(false);
                                    }
                                  }}
                                  className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500/10 hover:bg-indigo-500/20 text-xs font-bold text-indigo-500 rounded-xl border border-indigo-500/20 transition-all active:scale-95"
                                >
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  Test Me
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
            )}
            
            {/* Test Your Knowledge Button */}
            {currentLevelLessons && currentLevelLessons.length > 0 && (
            <div className="mt-12 flex justify-center">
              <button
                id="test-knowledge-button"
                onClick={async () => {
                  setIsQuizModalOpen(true);
                  setQuizError('');
                  
                  let quizToUse = currentQuiz;
                  if (!quizToUse || quizToUse.length === 0) {
                    setIsGeneratingQuiz(true);
                    try {
                      const depthContent = (currentContent as any)?.[depth]?.audio || currentContent;
                      const newQuiz = await generateQuizForLesson(depthContent, (currentContent as any).visuals || []);
                      setGeneratedQuizzes(prev => ({ ...prev, [currentQuizKey]: newQuiz }));
                      quizToUse = newQuiz;
                    } catch (err) {
                      setQuizError('Failed to generate quiz. Please try again.');
                      quizToUse = [];
                    } finally {
                      setIsGeneratingQuiz(false);
                    }
                  }
                  setQuizAnswers(new Array(quizToUse?.length || 0).fill(-1));

      if (isDrivingModeRef.current && quizToUse && quizToUse.length > 0) {
        beginDrivingQuiz(quizToUse);
      }
                }}
                disabled={isGeneratingQuiz}
                className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 text-lg w-full max-w-md disabled:opacity-50"
              >
                {isGeneratingQuiz ? <Loader2 className="h-6 w-6 animate-spin" /> : <CheckCircle className="h-6 w-6" />}
                {isGeneratingQuiz ? 'Generating Quiz...' : (checkIsLastLesson() && depth === 'advanced' ? 'Final Knowledge Check' : 'Take Knowledge Check')}
              </button>
            </div>
            )}
          </div>
          
          <div className="bg-bg-alt/50 px-8 py-6 border-t border-border-main flex flex-wrap justify-between items-center gap-4 relative">
            {isWaitingForVoiceCommand && (
              <div className="absolute inset-0 bg-indigo-500/10 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-2xl animate-pulse">
                <div className="flex items-center gap-3 text-indigo-500 font-medium">
                  <Mic className="h-6 w-6 animate-bounce" />
                  Listening for command (Yes, No, Skip, Next)...
                </div>
              </div>
            )}
            <button
              onClick={handlePrev}
              disabled={isFirstLesson}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-text-alt hover:text-text-main hover:bg-bg-alt disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
              Previous
            </button>

            {currentLevelLessons && currentLevelLessons.length > 0 && (
              <button
                onClick={() => {
                   const isFlexible = (course as any)?.progressionMode === 'flexible' || !(course as any)?.progressionMode;
                   const depthToMasteryKey = `${enrollment.currentLevel}_${enrollment.currentLessonIndex}_${depth}`;
                   const hasCompletedAnyMastery = Object.keys(lessonMastery).length > 0; // rough heuristic if they've taken tests

                   if (!hasCompletedAnyMastery) {
                      if (isFlexible) {
                         setShowCourseCompleteModal(false); 
                         setIsQuizModalOpen(false);
                         setShowSkipWarningModal(true);
                      } else {
                         setShowCourseCompleteModal(false); 
                         setIsQuizModalOpen(false);
                         setShowLockedWarningModal(true);
                      }
                   } else {
                      if (checkIsLastLesson() && depth === 'advanced') {
                          setShowCourseCompleteModal(true);
                      } else {
                          moveToNextStage();
                      }
                   }
                }}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white transition-colors shadow-lg ${checkIsLastLesson() && depth === 'advanced' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20'}`}
              >
                {nextDest.label}
                {checkIsLastLesson() && depth === 'advanced' ? <CheckCircle className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </button>
            )}

            {enrollment.completed && (
              <div className="flex items-center gap-2 text-emerald-500 font-medium px-6 py-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                <CheckCircle className="h-5 w-5" />
                Course Completed
              </div>
            )}
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-bg-alt rounded-2xl shadow-2xl border border-border-main p-8">
          <h2 className="text-2xl font-bold text-text-main mb-6 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-indigo-500" />
            Course Discussion
          </h2>
          
          <form onSubmit={handleAddComment} className="mb-8">
            <div className="flex gap-4">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ask a question or share your thoughts..."
                className="flex-1 px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt opacity-60 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Post
              </button>
            </div>
          </form>

          <div className="space-y-6">
            {comments.length === 0 ? (
              <p className="text-text-alt text-center py-4">No comments yet. Be the first to start the discussion!</p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="bg-bg-main/50 p-4 rounded-xl border border-border-main">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-indigo-500">{comment.userName}</span>
                    <span className="text-xs text-text-alt opacity-60">
                      {comment.createdAt?.toDate ? comment.createdAt.toDate().toLocaleDateString() : 'Just now'}
                    </span>
                  </div>
                  <p className="text-text-alt">{comment.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      {isQuizModalOpen && currentQuiz && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-bg-alt rounded-2xl shadow-2xl border border-border-main w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-6 border-b border-border-main flex justify-between items-start bg-bg-main/50">
              <div>
                <h2 className="text-2xl font-bold text-text-main">{isReviewQuiz ? 'Spaced Repetition Review' : 'Knowledge Check'}</h2>
                {!isReviewQuiz && enrollment && course && (
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs font-medium text-text-alt uppercase tracking-wider">
                    <span>{course.title}</span> {/* Wait, is there course.title? yes */}
                    <span className="text-border-main">•</span>
                    <span>Module {enrollment.currentLevel === 'explorer' ? '1' : enrollment.currentLevel === 'apprentice' ? '2' : '3'}</span>
                    <span className="text-border-main">•</span>
                    <span className="truncate max-w-[200px]" title={lessonTitle}>{lessonTitle}</span>
                    <span className="text-border-main">•</span>
                    <span className="text-indigo-400 capitalize">{depth}</span>
                  </div>
                )}
              </div>
              <button onClick={() => { setIsQuizModalOpen(false); resetTempVisualQuiz(); }} className="text-text-alt hover:text-text-main transition-colors mt-1">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-8">
              {isGeneratingQuiz ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Loader2 className="h-12 w-12 text-indigo-500 animate-spin mb-4" />
                  <p className="text-text-alt">{isReviewQuiz ? 'Preparing your review session...' : 'Generating your knowledge check...'}</p>
                </div>
              ) : (isReviewQuiz ? generatedQuizzes['review-session'] : currentQuiz).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-20 h-20 bg-rose-500/20 rounded-full flex items-center justify-center mb-6">
                    <X className="h-10 w-10 text-rose-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-text-main mb-2">Quiz Generation Failed</h3>
                  <p className="text-text-alt max-w-md mb-8">
                    We couldn't generate the quiz questions. You can try again or skip for now.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-sm">
                    <button
                      onClick={() => triggerQuiz(isReviewQuiz, true)}
                      className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors shadow-lg shadow-indigo-500/20"
                    >
                      Try Again
                    </button>
                    {(isDrivingModeRef.current || (course as any)?.progressionMode === 'flexible') && (
                      <button
                        onClick={() => {
                          setIsQuizModalOpen(false);
                          moveToNextStage();
                        }}
                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-500/20"
                      >
                        Skip for Now (Continue)
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsQuizModalOpen(false);
                        if (isDrivingModeRef.current) {
                           setIsDrivingMode(false);
                        }
                      }}
                      className="w-full py-3 bg-bg-main hover:bg-bg-alt text-text-main font-medium rounded-xl border border-border-main transition-colors"
                    >
                      {isDrivingModeRef.current ? 'Exit Driving Mode' : 'Close'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-text-alt">{isReviewQuiz ? 'Quickly review these concepts to maintain long-term memory.' : 'Answer the following questions based on the current lesson.'}</p>

                  {isDrivingMode && drivingQuizStatus && (
                    <div className="p-4 rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-300 text-sm">
                      <p className="font-bold mb-1">Driving Mode Voice Quiz</p>
                      <p>{drivingQuizStatus}</p>
                      <p className="mt-1 opacity-80">
                        Question {drivingQuizQuestionIndex + 1}
                        {drivingQuizAttemptCount > 0 ? ` • Attempt ${drivingQuizAttemptCount} of 3` : ''}
                      </p>
                    </div>
                  )}

                  
                  <div className="space-y-8">
                    {(isReviewQuiz ? generatedQuizzes['review-session'] || [] : currentQuiz).map((q: QuizQuestion, qIndex: number) => {
                      const normalize = (s: string) => s?.toLowerCase().replace(/\s+/g, ' ').trim();
                      const selectedAnswer = quizAnswers[qIndex];
                      const isAnswered = selectedAnswer !== -1;
                      
                      const checkCorrect = (idx: number) => {
                        if (idx === q.correctAnswerIndex) return true;
                        if (q.correctAnswerIndex === undefined && q.options && (q as any).correctAnswer) {
                           return normalize(q.options[idx]) === normalize((q as any).correctAnswer);
                        }
                        return false;
                      };

                      const isCorrect = isAnswered && checkCorrect(selectedAnswer);

                      // Data Integrity Check
                      const isMalformed = !q.options || q.options.length === 0 || (q.correctAnswerIndex === undefined && !(q as any).correctAnswer);

                      if (isMalformed) {
                        return (
                          <div key={qIndex} className="bg-rose-500/5 p-6 rounded-xl border border-rose-500/20 flex flex-col items-center gap-4 text-center">
                            <AlertCircle className="h-8 w-8 text-rose-500" />
                            <div>
                              <p className="text-text-main font-medium">This specific question could not be fully synthesized.</p>
                              <p className="text-xs text-text-alt opacity-60 mt-1">Our teaching engine encountered a complex formatting issue. Try reloading.</p>
                            </div>
                            {!isMobileDevice() && (
                              <button 
                                onClick={() => triggerQuiz(isReviewQuiz, true)}
                                className="px-4 py-2 bg-bg-alt hover:bg-bg-main text-xs font-bold text-indigo-500 rounded-lg border border-border-main transition-all uppercase tracking-widest"
                              >
                                Regenerate Quiz
                              </button>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div key={qIndex} className="bg-bg-main/50 p-6 rounded-xl border border-border-main">
                          <h3 className="text-lg font-medium text-text-main mb-4 flex gap-2">
                             <span>{qIndex + 1}.</span>
                            <div className="flex-1 prose prose-theme prose-sm max-w-none text-text-main" data-clarity-mask="true">
                               <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{q.question}</Markdown>
                             </div>
                          </h3>
                          <div className="space-y-3">
                            {q.options?.map((opt: string, oIndex: number) => {
                              const isSelected = selectedAnswer === oIndex;
                              
                              let optionClass = 'bg-bg-alt border-border-main text-text-alt hover:bg-bg-main';
                              let iconClass = 'border-border-main';
                              
                              if (isSelected) {
                                if (isCorrect) {
                                  optionClass = 'bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400';
                                  iconClass = 'border-emerald-500';
                                } else {
                                  optionClass = 'bg-rose-500/10 border-rose-500 text-rose-600 dark:text-rose-400';
                                  iconClass = 'border-rose-500';
                                }
                              }

                              return (
                                <label key={oIndex} className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${isCorrect ? 'cursor-default' : 'cursor-pointer'} ${optionClass}`}>
                                  <input 
                                    type="radio" 
                                    name={`question-${qIndex}`} 
                                    className="hidden"
                                    checked={isSelected}
                                    disabled={isCorrect}
                                    onChange={async () => {
                                      if (isCorrect) return;
                                      const newAnswers = [...quizAnswers];
                                      newAnswers[qIndex] = oIndex;
                                      setQuizAnswers(newAnswers);
                                      setQuizError('');

                                      // Update mastery and quiz attempts
                                      if (user && courseId) {
                                        const isNowCorrect = checkCorrect(oIndex);
                                        
                                        // increment quiz attempt
                                        try {
                                          const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
                                          await setDoc(enrollmentRef, {
                                            quizAttempts: increment(1),
                                            lastAccessed: serverTimestamp()
                                          }, { merge: true });
                                        } catch(e) { console.error('Error incrementing quiz attempts', e); }

                                        console.log(`[Quiz Validation] Q: ${q.question.slice(0, 50)}...`);
                                        console.log(`[Quiz Validation] Selected: ${opt} (Index: ${oIndex})`);
                                        console.log(`[Quiz Validation] Expected Index: ${q.correctAnswerIndex}`);
                                        console.log(`[Quiz Validation] Result: ${isNowCorrect ? 'CORRECT' : 'WRONG'}`);

                                        Diagnostics.logStep(GoldenPathStep.QUIZ_TAKEN, 'success', { isCorrect: isNowCorrect, questionIndex: qIndex });

                                        const qMetadata = { difficulty: (q as any).difficulty, type: (q as any).type };
                                        
                                        if (isReviewQuiz) {
                                          const reviewConcept = dueReviews[qIndex]?.conceptId;
                                          if (reviewConcept) {
                                            await updateConceptMastery(user.uid, courseId, reviewConcept, isNowCorrect ? 'correct_quiz' : 'incorrect_quiz', qMetadata);
                                          }
                                        } else {
                                          // Update all concepts in this lesson (approximation)
                                          if (currentLessonStructure?.concepts) {
                                            for (const concept of currentLessonStructure.concepts) {
                                              await updateConceptMastery(user.uid, courseId, concept.concept_title, isNowCorrect ? 'correct_quiz' : 'incorrect_quiz', qMetadata);
                                            }
                                          }

                                          // Update objectives based on lesson mastery
                                          if (currentLessonStructure) {
                                            const newScores = await getMasteryForLesson(user.uid, courseId, currentLessonStructure.concepts || []);
                                            const avgScore = Object.values(newScores).reduce((a, b) => a + b, 0) / (Object.values(newScores).length || 1);

                                            if (currentLessonStructure.learning_objectives) {
                                              for (const [idx, objText] of currentLessonStructure.learning_objectives.entries()) {
                                                await updateLearningObjective(user.uid, courseId, (currentLessonStructure as any).lessonId || currentLessonStructure.lesson_title, { id: `obj_${idx}`, text: objText }, avgScore);
                                              }
                                            }
                                            setLessonMastery(newScores);
                                          }
                                        }
                                      }
                                    }}
                                  />
                                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${iconClass}`}>
                                    {isSelected && <div className={`w-2.5 h-2.5 rounded-full ${isCorrect ? 'bg-emerald-500' : 'bg-rose-500'}`} />}
                                  </div>
                                  <span>{opt}</span>
                                </label>
                              );
                            })}
                          </div>
                          {isAnswered && (
                            <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${isCorrect ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'}`}>
                              {isCorrect ? <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <X className="w-5 h-5 shrink-0 mt-0.5" />}
                              <div className="flex-1">
                                <p className="font-bold mb-1">{isCorrect ? 'Correct!' : 'Incorrect'}</p>
                                {q.explanation && (
                                  <div className="prose prose-theme prose-sm max-w-none opacity-90 text-current" data-clarity-mask="true">
                                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{q.explanation}</Markdown>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {quizError && (
                    <div className="mt-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-500 text-sm">
                      {quizError}
                    </div>
                  )}

                  {(isReviewQuiz ? generatedQuizzes['review-session'] || [] : currentQuiz).every((q: QuizQuestion, i: number) => quizAnswers[i] === q.correctAnswerIndex) && (isReviewQuiz ? generatedQuizzes['review-session'] || [] : currentQuiz).length > 0 && (
                    <div className="mt-8 flex flex-col items-end gap-3">
                      {isReviewQuiz ? (
                        <button
                          onClick={() => { setIsQuizModalOpen(false); resetTempVisualQuiz(); }}
                          className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-bg-main font-black rounded-xl transition-all shadow-lg shadow-amber-500/20"
                        >
                          Finish Review
                        </button>
                      ) : (
                        <>
                          {Object.values(lessonMastery).some(s => s < MASTERY_THRESHOLD) ? (
                            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-amber-600 dark:text-amber-400 text-sm max-w-md text-right">
                              <p className="font-bold flex items-center justify-end gap-2">
                                Reinforcement Suggestion
                              </p>
                              <p className="mt-1">Some concepts need more practice. We recommend reviewing the tutor's latest explanations before advancing.</p>
                            </div>
                          ) : null}
                          <button
                            onClick={() => {
                              setIsQuizModalOpen(false);
                              resetTempVisualQuiz();
                              if (user && courseId && enrollment) {
                                  const enrollmentRef = doc(db, 'enrollments', `${user.uid}_${courseId}`);
                                  setDoc(enrollmentRef, {
                                      knowledgeCheckCompletedLessonIds: arrayUnion(`${enrollment.currentLevel}_${enrollment.currentLessonIndex}_${depth}`)
                                  }, { merge: true }).catch(err => console.error("Failed to track knowledge check completion:", err));
                              }
                              
                              handleMarkAsComplete();
                            }}
                            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-500/20 flex items-center gap-2"
                          >
                            {nextDest.label}
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Grounded Tutor Chatbot */}
      {currentLessonStructure && (
        <GroundedTutor 
          courseId={courseId || ''} 
          lessonId={`${enrollment.currentLevel}_${enrollment.currentLessonIndex}`}
          lessonContent={(currentContent as any)?.[depth]?.audio || (typeof currentContent === 'string' ? currentContent : '')}
          currentLevel={depth}
          concepts={typeof currentLessonStructure !== 'string' ? currentLessonStructure?.concepts : []}
          sourceExcerpts={typeof currentLessonStructure !== 'string' ? (currentLessonStructure?.concepts || []).flatMap((c: any) => c.sources?.map((s: any) => s.source_excerpt) || []) : []}
          quizPerformance={quizPerformance}
          visuals={[]}
        />
      )}
      {/* Sidebar: Learning Objectives */}
      <aside className="hidden xl:flex w-80 flex-col border-l border-border-main bg-bg-alt/20 shrink-0">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest">Learning Objectives</h2>
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500">
              <Brain className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-4">
            {(currentLessonStructure && typeof currentLessonStructure !== 'string' ? (currentLessonStructure.learning_objectives || [
              `Explain ${currentLessonStructure.lesson_title || 'this topic'}`,
              `Apply concepts of ${currentLessonStructure.lesson_title || 'this topic'}`
            ]) : [`Study ${lessonTitle}`]).map((obj: string, i: number) => {
              const matchedObj = learningObjectives.find(lo => lo.text === obj || lo.objectiveId === `obj_${i}`);
              const isCompleted = matchedObj?.status === 'completed';
              const isPartial = matchedObj?.status === 'partial';

              return (
                <div key={i} className="flex gap-4 items-start group">
                  <div className={`mt-1 w-6 h-6 rounded-lg flex items-center justify-center text-[10px] transition-colors shrink-0 font-bold border ${
                    isCompleted ? 'bg-emerald-500/20 border-emerald-500 text-emerald-500' : 
                    isPartial ? 'bg-amber-500/20 border-amber-500 text-amber-500' :
                    'bg-bg-alt border-border-main text-text-alt group-hover:border-indigo-500/50'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-3 h-3" /> : i + 1}
                  </div>
                  <div>
                    <p className={`text-sm leading-relaxed font-medium ${isCompleted ? 'text-text-alt opacity-60 line-through' : 'text-text-alt'}`}>
                      {obj}
                    </p>
                    {isPartial && !isCompleted && (
                      <span className="text-[10px] text-amber-500 font-bold uppercase tracking-tighter">Partial Mastery</span>
                    )}
                  </div>
                </div>
              );
            })}
            {!currentLessonStructure && <p className="text-sm text-text-alt opacity-60">No objectives available for this lesson.</p>}
          </div>
        </div>
        
        <div className="mt-auto p-6 border-t border-border-main">
          <div className="bg-indigo-500/10 rounded-2xl p-5 border border-indigo-500/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-black text-indigo-500 uppercase tracking-tighter">Lesson Mastery</span>
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              </div>
            </div>
            <div className="text-3xl font-black text-text-main mb-4 tracking-tight">
              {Math.round(Object.values(lessonMastery).reduce((a, b) => a + b, 0) / (Object.values(lessonMastery).length || 1))}%
            </div>
            <div className="h-2 w-full bg-bg-alt rounded-full overflow-hidden border border-border-main">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000"
                style={{ width: `${Math.round(Object.values(lessonMastery).reduce((a, b) => a + b, 0) / (Object.values(lessonMastery).length || 1))}%` }}
              />
            </div>
          </div>
        </div>
      </aside>
    </div>

    {/* Review Mode Overlay */}
      {isReviewMode && (
        <div className="fixed inset-0 z-[100] bg-bg-main/98 backdrop-blur-2xl p-8 flex flex-col">
          <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col mt-12 pb-12">
            <div className="flex items-center justify-between mb-12">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-3xl bg-amber-500/20 flex items-center justify-center text-amber-500 shadow-lg shadow-amber-500/10">
                  <Brain className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl font-black tracking-tight text-text-main">Review Session</h2>
                  <p className="text-text-alt font-medium tracking-wide">Time to reinforce what you've learned</p>
                </div>
              </div>
              <button 
                onClick={() => setIsReviewMode(false)} 
                className="p-3 bg-bg-alt/50 text-text-alt hover:bg-bg-alt hover:text-text-main rounded-2xl transition-all"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dueReviews.map((review, i) => (
                <div key={review.reviewId || i} className="p-8 rounded-3xl bg-bg-alt border border-border-main hover:border-amber-500/40 transition-all group relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Clock className="w-24 h-24" />
                  </div>
                  <h4 className="font-black text-xl mb-4 tracking-tight text-text-main">{review.conceptId}</h4>
                  <div className="flex items-center gap-6 text-sm text-text-alt mb-8 font-bold uppercase tracking-wider">
                    <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-indigo-500" /> Mastery: {review.masteryScore}%</span>
                    <span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" /> Improvement: {globalProfile?.momentum?.improvementRate || 0}%</span>
                  </div>
                  <button 
                     onClick={() => {
                       setIsReviewMode(false);
                       triggerQuiz(true);
                     }}
                     className="w-full py-4 bg-amber-500 text-bg-main font-black rounded-2xl hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/20 hover:-translate-y-1 active:scale-[0.98]"
                  >
                    START REVISION
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    {/* Driving Mode Auto Prompt Overlay */}
      {showDrivingPrompt && isDrivingMode && (
        <div className="fixed inset-0 z-[100] bg-bg-main/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-500">
          <div className="bg-bg-alt border border-indigo-500/50 rounded-3xl p-8 max-w-lg w-full shadow-2xl shadow-indigo-500/20 text-center space-y-8">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-indigo-500/20 rounded-full flex items-center justify-center text-indigo-500 animate-pulse">
                <Brain className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-text-main tracking-tight">
                {checkIsLastLesson() && depth === 'advanced' ? 'Course Complete' : depth === 'advanced' ? 'Stage Complete' : 'Review Time'}
              </h2>
              <p className="text-text-alt font-medium">
                {checkIsLastLesson() && depth === 'advanced'
                  ? 'Great work. You have completed this course. Would you like to take the final assessment now?'
                  : depth === 'advanced' 
                  ? 'Great job completing this lesson. Ready for the final assessment?' 
                  : 'Would you like to take a quick quiz or move to the next stage?'}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => {
                  console.log("User selected: Test");
                  if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
                  setShowDrivingPrompt(false);
                  triggerQuiz();
                }}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-3"
              >
                <CheckCircle className="w-5 h-5" />
                {checkIsLastLesson() && depth === 'advanced' ? 'Take final assessment' : depth === 'advanced' ? 'Take Final Assessment' : 'Take Quick Test'}
              </button>
              
              <button
                onClick={() => {
                  console.log("User selected: Next Lesson / Skip");
                  if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
                  setShowDrivingPrompt(false);
                  if (checkIsLastLesson() && depth === 'advanced') {
                    setShowCourseCompleteModal(true);
                    saveProgress(enrollment!.currentLevel, enrollment!.currentLessonIndex, true);
                  } else if (depth !== 'advanced') {
                    autoPlayRef.current = true;
                    moveToNextStage();
                  } else {
                    speakText("Lesson complete. Please leave a review.");
                  }
                }}
                className="w-full py-4 bg-bg-main hover:bg-bg-main/80 text-text-main font-bold rounded-2xl border border-border-main transition-all flex items-center justify-center gap-3"
              >
                <ChevronRight className="w-5 h-5" />
                {checkIsLastLesson() && depth === 'advanced' ? 'Not now' : depth === 'advanced' ? 'Skip Assessment' : 'Next Lesson'}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 text-indigo-500/60 text-xs font-mono animate-pulse">
              <Mic className="w-4 h-4" />
              <span>Listening for "Yes" or "No"...</span>
            </div>
          </div>
        </div>
      )}
      {showSkipWarningModal && (
        <div className="fixed inset-0 z-50 bg-bg-main/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-bg-alt border border-border-main rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <h2 className="text-2xl font-bold text-text-main mb-4 tracking-tight">Skip Knowledge Check?</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              You have not completed the knowledge check for this lesson. You can continue now and return to it later.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowSkipWarningModal(false);
                  moveToNextStage();
                }}
                className="w-full py-3 bg-bg-main hover:bg-bg-main/80 border border-border-main text-text-main font-bold rounded-xl transition-all"
              >
                Continue Anyway
              </button>
              <button
                onClick={() => {
                  setShowSkipWarningModal(false);
                  setIsQuizModalOpen(true);
                  setQuizError('');
                  const currentQuizKey = `${courseId}-${enrollment?.currentLevel}-${enrollment?.currentLessonIndex}-${depth}`;
                  let quizToUse = generatedQuizzes[currentQuizKey] || currentQuiz;
                  setQuizAnswers(new Array(quizToUse?.length || 0).fill(-1));
                }}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20"
              >
                Take Knowledge Check
              </button>
            </div>
          </div>
        </div>
      )}

      {showLockedWarningModal && (
        <div className="fixed inset-0 z-[100] bg-bg-main/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-300">
          <div className="bg-bg-alt border border-rose-500/50 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <h2 className="text-2xl font-bold text-text-main mb-4 tracking-tight">Level Locked</h2>
            <p className="text-text-alt mb-8 leading-relaxed">
              This course requires you to complete the knowledge check before moving forward to the next lesson.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowLockedWarningModal(false);
                  setIsQuizModalOpen(true);
                  setQuizError('');
                  const currentQuizKey = `${courseId}-${enrollment?.currentLevel}-${enrollment?.currentLessonIndex}-${depth}`;
                  let quizToUse = generatedQuizzes[currentQuizKey] || currentQuiz;
                  setQuizAnswers(new Array(quizToUse?.length || 0).fill(-1));
                }}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20"
              >
                Take Knowledge Check
              </button>
              <button
                onClick={() => setShowLockedWarningModal(false)}
                className="w-full py-3 bg-bg-main hover:bg-bg-main/80 border border-border-main text-text-main font-bold rounded-xl transition-all"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {showCourseCompleteModal && (
        <div className="fixed inset-0 z-[100] bg-bg-main/98 backdrop-blur-2xl flex items-center justify-center p-6 animate-in fade-in duration-500">
          <div className="bg-bg-alt border border-emerald-500/50 rounded-3xl p-10 max-w-2xl w-full shadow-2xl shadow-emerald-500/20 text-center space-y-8">
            <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 mx-auto mb-6">
              <CheckCircle className="w-12 h-12" />
            </div>
            <h2 className="text-4xl font-black text-text-main tracking-tight">Course Completed</h2>
            <p className="text-xl text-text-alt font-medium max-w-lg mx-auto">
              Thank you for taking this course. Your progress has been saved. 
              You can return anytime to review the lessons or take the final assessment later.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-8">
              <button
                onClick={() => { setShowCourseCompleteModal(false); navigate('/'); }}
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
              >
                Back to dashboard
              </button>
              <button
                onClick={() => { setShowCourseCompleteModal(false); }}
                className="w-full py-4 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-500 font-black rounded-2xl transition-all shadow-lg shadow-indigo-500/5 flex items-center justify-center gap-2"
              >
                Leave a review
              </button>
              <button
                onClick={() => { setShowCourseCompleteModal(false); }}
                className="w-full py-4 bg-bg-main hover:bg-bg-main/80 text-text-main font-bold rounded-2xl border border-border-main transition-all flex items-center justify-center gap-2"
              >
                Review course
              </button>
              <button
                onClick={() => { setShowCourseCompleteModal(false); }}
                className="w-full py-4 bg-bg-main hover:bg-bg-main/80 text-text-main font-bold rounded-2xl border border-border-main transition-all flex items-center justify-center gap-2"
              >
                Take assessment later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
